/*
 * Theme adapter for window.TM24SearchCore. It owns Shopify shard loading,
 * completeness checks, current/filtered collection sets, server-card hydration,
 * query-only combobox UI and browser state. Normalization, vocabulary,
 * suggestions and ranking remain in the single shared core.
 */
(() => {
  'use strict';

  const tm24PerfNow = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : 0
  );
  const tm24Perf = {
    controllerStartMs: tm24PerfNow(),
    indexShards: [],
    versionShards: [],
  };
  const publishTm24Perf = (root = null) => {
    const payload = JSON.stringify({ ...tm24Perf, publishedMs: tm24PerfNow() });
    if (root) root.setAttribute('data-tm24-performance', payload);
    else document.querySelectorAll('[data-tm24-search-root]').forEach((node) => {
      node.setAttribute('data-tm24-performance', payload);
    });
  };

  const GLOBAL_KEY = '__tm24UnifiedSearchControllerV2';
  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].mount(document);
    return;
  }

  const INDEX_SECTION = 'tm24-unified-search-index-shard';
  const INDEX_VERSION_SECTION = 'tm24-unified-search-version-shard';
  const CARD_SECTION = 'tm24-unified-search-card-shard';
  const NATIVE_SECTION = 'tm24-unified-search-native-shard';
  const INDEX_SCHEMA = 'tm24-index-v3';
  const INDEX_VERSION_SCHEMA = 'tm24-index-version-v1';
  const NATIVE_SCHEMA = 'tm24-native-v3';
  const FINDER_FILTERS_PARAM = 'tm24_filters';
  const FINDER_FILTER_AXES = Object.freeze([
    Object.freeze({ key: 'manufacturer', field: 'manufacturer', short: 'm', labelData: 'filterManufacturer' }),
    Object.freeze({ key: 'gateTypes', field: 'gateType', short: 'g', labelData: 'filterGateType' }),
    Object.freeze({ key: 'productTypes', field: 'productType', short: 'p', labelData: 'filterProductType' }),
  ]);
  const MAX_QUERY_LENGTH = 120;
  const MAX_SUGGESTIONS = 6;
  const INDEX_PAGE_SIZE = 250;
  const INDEX_CONCURRENCY = 9;
  const RELEVANCE_SORT = 'tm24-relevance';
  const MAX_INDEX_CACHE_ENTRIES = 8;
  const MAX_CARD_CACHE_ENTRIES = 24;
  const MAX_SESSION_INDEX_ENTRIES = 4;
  const MAX_SESSION_INDEX_BYTES = 4_800_000;
  const MAX_SESSION_INDEX_PRODUCTS = 1_000;
  const FINDER_PRODUCT_TYPE_INITIAL = 10;
  const FINDER_LONG_GROUP_MINIMUM = 13;
  const FINDER_MOBILE_MEDIA = '(max-width: 749px)';
  const SHARD_REQUEST_TIMEOUT = 7_500;
  const SHARD_RETRY_LIMIT = 1;
  const COMPLETE_INDEX_DEADLINE = 18_000;
  const VERSION_VALIDATION_DEADLINE = 12_000;
  const MORE_CLICK_GUARD_MS = 650;
  const SESSION_PREFIX = 'tm24:unified-search:index:v3:';
  const SESSION_INDEX_LIST = `${SESSION_PREFIX}keys`;
  const SESSION_ID_KEY = `${SESSION_PREFIX}browser-session`;
  const PERSISTENT_CACHE_SCHEMA = 'tm24-session-index-v1';
  const PERSISTENT_DB_NAME = 'tm24-unified-search-session-v1';
  const PERSISTENT_STORE_NAME = 'indexes';
  const INDEX_UPDATED_EVENT = 'tm24:index-updated';
  const INDEX_VALIDATION_ERROR_EVENT = 'tm24:index-validation-error';
  const instances = new WeakMap();
  const productFilterValuesCache = new WeakMap();
  const indexCache = new Map();
  const indexInflight = new Map();
  const indexValidationInflight = new Map();
  const indexValidationAttempted = new Set();
  const indexValidatedThisDocument = new Set();
  const cardCache = new Map();
  let persistentDbPromise = null;

  const core = () => window.TM24SearchCore;
  const locale = () => (document.documentElement.lang || 'de').split('-')[0].toLowerCase();
  const currency = () => window.Shopify?.currency?.active || '';
  const market = () => window.Shopify?.country || document.documentElement.dataset.country || '';
  const now = () => Date.now();
  const controllerScript = document.currentScript
    || document.querySelector('script[src*="tm24-unified-search-controller"]');
  const CONTROLLER_BUILD = controllerScript?.src || 'inline-controller';

  // These labels are a deliberately small, audited DE presentation map for
  // real ASCII-only metafield values in the 2026-08-12 Finder snapshot. The
  // comparison key still comes from the shared core; NL/FR keep source labels.
  const DE_FILTER_DISPLAY_ALIASES = Object.freeze({
    manufacturer: Object.freeze({
      hoermann: 'Hörmann',
    }),
    gateTypes: Object.freeze({
      tuer: 'Tür',
    }),
    productTypes: Object.freeze({
      abhaengeprofil: 'Abhängeprofil',
      empfaenger: 'Empfänger',
      funkempfaenger: 'Funkempfänger',
      gehaeuse: 'Gehäuse',
      hydraulikoel: 'Hydrauliköl',
      naeherungsschalter: 'Näherungsschalter',
      pendelstuetze: 'Pendelstütze',
      radfuehrung: 'Radführung',
      radialdaempfer: 'Radialdämpfer',
      schluesselschalter: 'Schlüsselschalter',
      schlupftuerschalter: 'Schlupftürschalter',
      sicherungsbuegel: 'Sicherungsbügel',
      standsaeule: 'Standsäule',
    }),
  });

  function cacheRead(cache, key) {
    const value = cache.get(key);
    if (value === undefined) return undefined;
    cache.delete(key);
    cache.set(key, value);
    return value;
  }

  function cacheWrite(cache, key, value, maximum) {
    cache.delete(key);
    cache.set(key, value);
    while (cache.size > maximum) {
      const oldest = cache.keys().next().value;
      cache.delete(oldest);
    }
    return value;
  }

  function cacheHash(value) {
    let hash = 2166136261;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16);
  }

  function sessionKey(sourceKey) {
    return `${SESSION_PREFIX}${cacheHash(sourceKey)}`;
  }

  function browserSessionId() {
    try {
      const storage = window.sessionStorage;
      if (!storage) return '';
      let identifier = storage.getItem(SESSION_ID_KEY);
      if (!identifier) {
        identifier = `${now().toString(36)}-${Math.random().toString(36).slice(2)}`;
        storage.setItem(SESSION_ID_KEY, identifier);
      }
      return identifier;
    } catch (_) {
      return '';
    }
  }

  function isFingerprint(value) {
    return /^[a-f0-9]{64}$/i.test(String(value || ''));
  }

  function validFingerprints(values, pages) {
    return Array.isArray(values)
      && values.length === pages
      && values.every(isFingerprint);
  }

  function persistentCacheKey(source, sessionId = browserSessionId()) {
    return sessionId ? `${sessionId}|${cacheHash(source.key)}` : '';
  }

  function openPersistentDatabase() {
    if (!window.indexedDB) return Promise.resolve(null);
    if (persistentDbPromise) return persistentDbPromise;
    persistentDbPromise = new Promise((resolve) => {
      let settled = false;
      const finish = (database) => {
        if (settled) return;
        settled = true;
        resolve(database);
      };
      let request;
      try {
        request = window.indexedDB.open(PERSISTENT_DB_NAME, 1);
      } catch (_) {
        finish(null);
        return;
      }
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(PERSISTENT_STORE_NAME)) {
          request.result.createObjectStore(PERSISTENT_STORE_NAME, { keyPath: 'cacheKey' });
        }
      };
      request.onsuccess = () => finish(request.result);
      request.onerror = () => finish(null);
      request.onblocked = () => finish(null);
    });
    return persistentDbPromise;
  }

  function restorePersistentIndex(record, source) {
    const pages = safeInteger(record?.pages);
    const expected = safeInteger(record?.expected);
    const pageSize = safeInteger(record?.pageSize);
    if (
      record?.cacheSchema !== PERSISTENT_CACHE_SCHEMA
      || record?.schema !== INDEX_SCHEMA
      || record?.key !== source.key
      || record?.path !== source.path
      || record?.complete !== true
      || !pages
      || !pageSize
      || !Array.isArray(record?.products)
      || record.products.length !== expected
      || !validFingerprints(record.fingerprints, pages)
    ) return null;
    const productsAreComplete = record.products.every((product) => (
      product
      && product.id != null
      && product.handle
      && product.url
      && product._tm24SourceKey === source.key
    ));
    if (!productsAreComplete) return null;
    return Object.freeze({
      key: record.key,
      path: record.path,
      params: String(record.params || ''),
      products: Object.freeze(record.products),
      expected,
      pages,
      pageSize,
      fingerprints: Object.freeze([...record.fingerprints]),
      fetchedAt: safeInteger(record.fetchedAt),
      validatedAt: safeInteger(record.validatedAt, safeInteger(record.fetchedAt)),
      complete: true,
      fromPersistent: true,
    });
  }

  async function readPersistentIndex(source) {
    const sessionId = browserSessionId();
    const cacheKey = persistentCacheKey(source, sessionId);
    if (!cacheKey) return null;
    const database = await openPersistentDatabase();
    if (!database) return null;
    const record = await new Promise((resolve) => {
      try {
        const transaction = database.transaction(PERSISTENT_STORE_NAME, 'readonly');
        const request = transaction.objectStore(PERSISTENT_STORE_NAME).get(cacheKey);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
        transaction.onabort = () => resolve(null);
      } catch (_) {
        resolve(null);
      }
    });
    if (record?.sessionId !== sessionId) return null;
    return restorePersistentIndex(record, source);
  }

  async function writePersistentIndex(dataset) {
    const sessionId = browserSessionId();
    const cacheKey = persistentCacheKey(dataset, sessionId);
    if (!cacheKey || !validFingerprints(dataset?.fingerprints, safeInteger(dataset?.pages))) return;
    const database = await openPersistentDatabase();
    if (!database) return;
    await new Promise((resolve) => {
      try {
        const transaction = database.transaction(PERSISTENT_STORE_NAME, 'readwrite');
        transaction.objectStore(PERSISTENT_STORE_NAME).put({
          cacheKey,
          cacheSchema: PERSISTENT_CACHE_SCHEMA,
          sessionId,
          schema: INDEX_SCHEMA,
          key: dataset.key,
          path: dataset.path,
          params: dataset.params,
          products: dataset.products,
          expected: dataset.expected,
          pages: dataset.pages,
          pageSize: dataset.pageSize,
          fingerprints: dataset.fingerprints,
          fetchedAt: dataset.fetchedAt,
          validatedAt: dataset.validatedAt,
          savedAt: now(),
          complete: true,
        });
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      } catch (_) {
        resolve();
      }
    });
  }

  function readSessionIndex(source) {
    try {
      const raw = window.sessionStorage?.getItem(sessionKey(source.key));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const pages = safeInteger(parsed?.pages);
      const pageSize = safeInteger(parsed?.pageSize);
      if (
        parsed?.schema !== INDEX_SCHEMA
        || parsed?.key !== source.key
        || parsed?.complete !== true
        || !pages
        || !pageSize
        || !Array.isArray(parsed?.products)
        || parsed.products.length !== safeInteger(parsed.expected)
        || !validFingerprints(parsed.fingerprints, pages)
      ) {
        window.sessionStorage?.removeItem(sessionKey(source.key));
        return null;
      }
      const sourceParams = String(parsed.params || '');
      const sourceProducts = parsed.products.map((storedProduct, index) => ({
        ...storedProduct,
        _tm24SourcePage: Math.floor(index / pageSize) + 1,
        _tm24SourceKey: parsed.key,
        _tm24SourcePath: parsed.path,
        _tm24SourceParams: sourceParams,
        _tm24Ordinal: index,
        _tm24CardPage: Math.floor(index / 50) + 1,
      }));
      return Object.freeze({
        key: parsed.key,
        path: parsed.path,
        params: parsed.params,
        products: Object.freeze(sourceProducts),
        expected: parsed.expected,
        pages,
        pageSize,
        fingerprints: Object.freeze([...parsed.fingerprints]),
        fetchedAt: parsed.fetchedAt,
        validatedAt: safeInteger(parsed.validatedAt, safeInteger(parsed.fetchedAt)),
        complete: true,
        fromSession: true,
      });
    } catch (_) {
      return null;
    }
  }

  function sessionPayload(dataset) {
    const compactProducts = dataset.products.map((product) => {
      const {
        _tm24SourcePage,
        _tm24SourceKey,
        _tm24SourcePath,
        _tm24SourceParams,
        _tm24Ordinal,
        _tm24CardPage,
        ...publicProduct
      } = product;
      return publicProduct;
    });
    return JSON.stringify({
      schema: INDEX_SCHEMA,
      key: dataset.key,
      path: dataset.path,
      params: dataset.params,
      products: compactProducts,
      expected: dataset.expected,
      pages: dataset.pages,
      pageSize: dataset.pageSize,
      fingerprints: dataset.fingerprints,
      fetchedAt: dataset.fetchedAt,
      validatedAt: dataset.validatedAt,
      complete: true,
    });
  }

  function writeSessionIndex(dataset) {
    try {
      if (safeInteger(dataset?.expected) > MAX_SESSION_INDEX_PRODUCTS) return;
      const payload = sessionPayload(dataset);
      if (payload.length > MAX_SESSION_INDEX_BYTES) return;
      const key = sessionKey(dataset.key);
      window.sessionStorage?.setItem(key, payload);
      let keys = [];
      try {
        keys = JSON.parse(window.sessionStorage?.getItem(SESSION_INDEX_LIST) || '[]');
      } catch (_) {
        keys = [];
      }
      keys = [key, ...(Array.isArray(keys) ? keys : []).filter((item) => item !== key)];
      keys.slice(MAX_SESSION_INDEX_ENTRIES).forEach((item) => window.sessionStorage?.removeItem(item));
      window.sessionStorage?.setItem(SESSION_INDEX_LIST, JSON.stringify(keys.slice(0, MAX_SESSION_INDEX_ENTRIES)));
    } catch (_) {
      // Session storage is an optional accelerator; Shopify remains authoritative.
    }
  }

  function sourceError(message, code = 'source_error') {
    const error = new Error(message);
    error.code = code;
    return error;
  }

  function safeInteger(value, fallback = 0) {
    const result = Number.parseInt(String(value ?? ''), 10);
    return Number.isSafeInteger(result) && result >= 0 ? result : fallback;
  }

  function interpolate(template, values) {
    let output = String(template || '');
    Object.entries(values).forEach(([key, value]) => {
      output = output.replaceAll(`__${key}__`, String(value ?? ''));
    });
    return output;
  }

  function appendHighlightedLabel(target, label, query) {
    const source = String(label || '');
    const needle = String(query || '').trim();
    const index = source.toLocaleLowerCase(locale()).indexOf(needle.toLocaleLowerCase(locale()));
    if (!needle || index < 0) {
      target.textContent = source;
      return;
    }
    target.append(document.createTextNode(source.slice(0, index)));
    const mark = document.createElement('mark');
    mark.textContent = source.slice(index, index + needle.length);
    target.append(mark, document.createTextNode(source.slice(index + needle.length)));
  }

  function canonicalParams({ filtered }) {
    const params = new URLSearchParams(location.search);
    [...params.keys()].forEach((key) => {
      if ([
        'page', 'section_id', 'view', 'q', 'tm24_q', 'tm24_correction',
        'tm24_visible', 'tm24_context', 'tm24_collection', 'tm24_filters',
      ].includes(key)) {
        params.delete(key);
      } else if (!filtered && key.startsWith('filter.')) {
        params.delete(key);
      }
    });
    if (params.get('sort_by') === RELEVANCE_SORT) params.delete('sort_by');
    params.sort();
    return params;
  }

  function hasActiveFilters(params) {
    return [...params.keys()].some((key) => key.startsWith('filter.'));
  }

  function normalizeFilterValue(value) {
    const shared = core()?.normalize?.(value);
    if (typeof shared === 'string') return shared;
    return String(value ?? '')
      .normalize('NFKC')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase(locale());
  }

  function finderFilterDisplayLabel(axisKey, value) {
    const label = String(value ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim();
    if (locale() !== 'de') return label;
    return DE_FILTER_DISPLAY_ALIASES[axisKey]?.[normalizeFilterValue(label)] || label;
  }

  function productFilterValues(product, axis) {
    if (product && typeof product === 'object') {
      let cached = productFilterValuesCache.get(product);
      if (!cached) {
        cached = {};
        productFilterValuesCache.set(product, cached);
      }
      if (cached[axis.key]) return cached[axis.key];
      const plural = product?.[axis.key];
      const source = Array.isArray(plural) ? plural : [product?.[axis.field]];
      cached[axis.key] = [...new Set(source.map(normalizeFilterValue).filter(Boolean))];
      return cached[axis.key];
    }
    const plural = product?.[axis.key];
    const source = Array.isArray(plural) ? plural : [product?.[axis.field]];
    return [...new Set(source.map(normalizeFilterValue).filter(Boolean))];
  }

  function emptyFinderFilters() {
    return Object.fromEntries(FINDER_FILTER_AXES.map((axis) => [axis.key, []]));
  }

  function parseFinderFilters(search = location.search, parsedFilters = null) {
    const filters = emptyFinderFilters();
    const raw = new URLSearchParams(search).get(FINDER_FILTERS_PARAM);
    if ((!raw || raw.length > 3000) && !parsedFilters) return filters;
    try {
      const parsed = parsedFilters && typeof parsedFilters === 'object'
        ? parsedFilters
        : JSON.parse(raw);
      FINDER_FILTER_AXES.forEach((axis) => {
        const values = Array.isArray(parsed?.[axis.short]) ? parsed[axis.short] : [];
        // The catalog currently contains more than 100 genuine product-type
        // values. Keep the URL guard byte-based above, but do not truncate a
        // valid, catalog-derived selection during history restoration.
        filters[axis.key] = [...new Set(values.map(normalizeFilterValue).filter(Boolean))];
      });
    } catch (_) {
      // Invalid customer-controlled state is treated as an empty filter set.
    }
    return filters;
  }

  function hasFinderFilters(filters) {
    return FINDER_FILTER_AXES.some((axis) => filters?.[axis.key]?.length > 0);
  }

  function serializeFinderFilters(filters) {
    const compact = {};
    FINDER_FILTER_AXES.forEach((axis) => {
      const values = [...new Set((filters?.[axis.key] || []).map(normalizeFilterValue).filter(Boolean))];
      if (values.length) compact[axis.short] = values;
    });
    return Object.keys(compact).length ? JSON.stringify(compact) : '';
  }

  function compactFinderFilters(filters) {
    const serialized = serializeFinderFilters(filters);
    return serialized ? JSON.parse(serialized) : {};
  }

  function filterLabelScore(label) {
    const letters = String(label || '').replace(/[^\p{L}]/gu, '');
    if (!letters) return 0;
    if (letters === letters.toLocaleLowerCase(locale())) return 0;
    if (letters.length > 4 && letters === letters.toLocaleUpperCase(locale())) return 1;
    return 2;
  }

  function buildFinderFilterCatalog(products) {
    const catalog = {};
    FINDER_FILTER_AXES.forEach((axis) => {
      const values = new Map();
      products.forEach((product) => {
        const rawValues = Array.isArray(product?.[axis.key]) ? product[axis.key] : [product?.[axis.field]];
        const normalizedValues = [];
        rawValues.forEach((rawValue) => {
          const label = String(rawValue ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim();
          const key = normalizeFilterValue(label);
          if (!key) return;
          normalizedValues.push(key);
          const entry = values.get(key) || { key, labels: new Map(), total: 0 };
          entry.labels.set(label, (entry.labels.get(label) || 0) + 1);
          entry.total += 1;
          values.set(key, entry);
        });
        if (product && typeof product === 'object') {
          let cached = productFilterValuesCache.get(product);
          if (!cached) {
            cached = {};
            productFilterValuesCache.set(product, cached);
          }
          cached[axis.key] = [...new Set(normalizedValues)];
        }
      });
      catalog[axis.key] = [...values.values()].map((entry) => {
        const labels = [...entry.labels.entries()].sort(([left, leftCount], [right, rightCount]) => (
          filterLabelScore(right) - filterLabelScore(left)
          || rightCount - leftCount
          || left.localeCompare(right, locale(), { sensitivity: 'variant' })
        ));
        const sourceLabel = labels[0]?.[0] || entry.key;
        return {
          key: entry.key,
          label: finderFilterDisplayLabel(axis.key, sourceLabel),
          total: entry.total,
        };
      }).sort((left, right) => left.label.localeCompare(right.label, locale(), { sensitivity: 'base', numeric: true }));
    });
    return catalog;
  }

  function productMatchesFinderFilters(product, filters, ignoredAxis = '', optionKey = '') {
    return FINDER_FILTER_AXES.every((axis) => {
      const selected = filters?.[axis.key] || [];
      const values = productFilterValues(product, axis);
      if (axis.key === ignoredAxis) {
        return !optionKey || values.includes(optionKey);
      }
      return selected.length === 0 || values.some((value) => selected.includes(value));
    });
  }

  function finderOptionCount(products, filters, axisKey, optionKey) {
    return products.reduce((count, product) => (
      count + (productMatchesFinderFilters(product, filters, axisKey, optionKey) ? 1 : 0)
    ), 0);
  }

  function routingOrder(routes) {
    return [...routes].sort((left, right) => (
      Number(right.count || 0) - Number(left.count || 0)
      || Number(right.bestScore || 0) - Number(left.bestScore || 0)
      || Number(left.nativeRank ?? Number.MAX_SAFE_INTEGER) - Number(right.nativeRank ?? Number.MAX_SAFE_INTEGER)
      || String(left.handle || '').localeCompare(String(right.handle || ''))
    )).slice(0, 1);
  }

  function buildFinderFacetCounts(products, filters, catalog) {
    const result = {};
    FINDER_FILTER_AXES.forEach((axis) => {
      const extras = new Map();
      products.forEach((product) => {
        const matchesOtherAxes = FINDER_FILTER_AXES.every((otherAxis) => {
          if (otherAxis.key === axis.key) return true;
          const otherSelected = filters?.[otherAxis.key] || [];
          if (!otherSelected.length) return true;
          const values = productFilterValues(product, otherAxis);
          return values.some((value) => otherSelected.includes(value));
        });
        if (!matchesOtherAxes) return;
        const values = productFilterValues(product, axis);
        values.forEach((value) => extras.set(value, (extras.get(value) || 0) + 1));
      });
      result[axis.key] = new Map((catalog?.[axis.key] || []).map((option) => [
        option.key,
        extras.get(option.key) || 0,
      ]));
    });
    return result;
  }

  function previewParams() {
    const params = new URLSearchParams();
    const current = new URLSearchParams(location.search);
    ['preview_theme_id'].forEach((key) => {
      if (current.has(key)) params.set(key, current.get(key));
    });
    return params;
  }

  function sourceKey(path, params, expected = 0) {
    const query = params.toString();
    return [
      location.host,
      locale(),
      market(),
      currency(),
      core()?.VERSION || 'missing-core',
      INDEX_SCHEMA,
      CONTROLLER_BUILD,
      safeInteger(expected),
      path,
      query,
    ].join('|');
  }

  function sectionUrl(path, params, sectionId, page, requestToken = '') {
    const url = new URL(path, location.origin);
    const merged = new URLSearchParams(params);
    merged.set('section_id', sectionId);
    merged.set('page', String(page));
    if (requestToken) merged.set('tm24_index_request', requestToken);
    url.search = merged.toString();
    return url;
  }

  async function fetchHtml(url, signal, { retries = 0 } = {}) {
    let attempt = 0;
    while (true) {
      try {
        return await fetchHtmlAttempt(url, signal);
      } catch (error) {
        if (isAbort(error) || signal?.aborted || attempt >= retries) throw error;
        attempt += 1;
      }
    }
  }

  async function fetchHtmlAttempt(url, signal) {
    const requestController = new AbortController();
    let timedOut = false;
    const abortFromParent = () => requestController.abort();
    if (signal?.aborted) requestController.abort();
    else signal?.addEventListener('abort', abortFromParent, { once: true });
    const timeout = window.setTimeout(() => {
      timedOut = true;
      requestController.abort();
    }, SHARD_REQUEST_TIMEOUT);
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: requestController.signal,
      });
      if (!response.ok) throw sourceError(`HTTP ${response.status}`, 'http_error');
      return await response.text();
    } catch (error) {
      if (timedOut) throw sourceError('Request timed out', 'timeout');
      throw error;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abortFromParent);
    }
  }

  async function withDeadline(signal, milliseconds, code, operation) {
    const deadlineController = new AbortController();
    let timedOut = false;
    const abortFromParent = () => deadlineController.abort();
    if (signal?.aborted) deadlineController.abort();
    else signal?.addEventListener('abort', abortFromParent, { once: true });
    const timeout = window.setTimeout(() => {
      timedOut = true;
      deadlineController.abort();
    }, milliseconds);
    try {
      return await operation(deadlineController.signal);
    } catch (error) {
      if (timedOut) throw sourceError('Index deadline exceeded', code);
      throw error;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abortFromParent);
    }
  }

  function parseIndexShard(html, requestedPage, source) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = documentFragment.querySelector('[data-tm24-search-shard]');
    const payload = wrapper?.querySelector('[data-tm24-search-products]');
    if (!wrapper || !payload) throw sourceError('Missing index shard markup', 'invalid_markup');
    if (wrapper.dataset.schemaVersion !== INDEX_SCHEMA) {
      throw sourceError('Unexpected index schema', 'schema_mismatch');
    }

    const page = safeInteger(wrapper.dataset.page);
    const pages = safeInteger(wrapper.dataset.pages);
    const expected = safeInteger(wrapper.dataset.expectedCount);
    const pageSize = safeInteger(wrapper.dataset.pageSize);
    const pageItems = safeInteger(wrapper.dataset.pageItems);
    const fingerprint = String(wrapper.dataset.fingerprint || '');
    if (!page || !pages || !pageSize || page !== requestedPage || page > pages) {
      throw sourceError('Invalid pagination metadata', 'pagination_mismatch');
    }
    if (!isFingerprint(fingerprint)) throw sourceError('Invalid index fingerprint', 'invalid_fingerprint');

    let products;
    try {
      products = JSON.parse(payload.textContent || '[]');
    } catch (_) {
      throw sourceError('Invalid index JSON', 'invalid_json');
    }
    if (!Array.isArray(products) || products.length !== pageItems) {
      throw sourceError('Index item count mismatch', 'item_count_mismatch');
    }

    products.forEach((product, index) => {
      if (!product || product.id == null || !product.handle || !product.url) {
        throw sourceError('Incomplete public product record', 'invalid_product');
      }
      product._tm24SourcePage = page;
      product._tm24SourceKey = source.key;
      product._tm24SourcePath = source.path;
      product._tm24SourceParams = source.params.toString();
      product._tm24Ordinal = ((page - 1) * pageSize) + index;
      product._tm24CardPage = Math.floor(product._tm24Ordinal / 50) + 1;
    });

    return { page, pages, expected, pageSize, fingerprint, products };
  }

  async function fetchIndexShard(source, page, signal, requestToken) {
    const startedAt = tm24PerfNow();
    let attempt = 0;
    const url = sectionUrl(source.path, source.params, INDEX_SECTION, page, requestToken);
    while (true) {
      try {
        const html = await fetchHtml(url, signal);
        const fetchedAt = tm24PerfNow();
        const shard = parseIndexShard(html, page, source);
        tm24Perf.indexShards[page - 1] = {
          page,
          startedAt,
          fetchedAt,
          parsedAt: tm24PerfNow(),
        };
        return shard;
      } catch (error) {
        if (isAbort(error) || signal?.aborted || attempt >= SHARD_RETRY_LIMIT) throw error;
        attempt += 1;
      }
    }
  }

  function parseIndexVersionShard(html, requestedPage) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = documentFragment.querySelector('[data-tm24-search-version-shard]');
    if (!wrapper) throw sourceError('Missing index version markup', 'invalid_version_markup');
    if (
      wrapper.dataset.schemaVersion !== INDEX_SCHEMA
      || wrapper.dataset.versionSchema !== INDEX_VERSION_SCHEMA
    ) throw sourceError('Unexpected index version schema', 'version_schema_mismatch');

    const page = safeInteger(wrapper.dataset.page);
    const pages = safeInteger(wrapper.dataset.pages);
    const pageSize = safeInteger(wrapper.dataset.pageSize);
    const pageItems = safeInteger(wrapper.dataset.pageItems);
    const expected = safeInteger(wrapper.dataset.expectedCount);
    const fingerprint = String(wrapper.dataset.fingerprint || '');
    if (!page || !pages || !pageSize || page !== requestedPage || page > pages) {
      throw sourceError('Invalid version pagination metadata', 'version_pagination_mismatch');
    }
    if (pageItems > pageSize || !isFingerprint(fingerprint)) {
      throw sourceError('Invalid index version fingerprint', 'invalid_version_fingerprint');
    }
    return { page, pages, pageSize, pageItems, expected, fingerprint };
  }

  async function fetchIndexVersionShard(source, page, signal, requestToken) {
    const startedAt = tm24PerfNow();
    let attempt = 0;
    const url = sectionUrl(source.path, source.params, INDEX_VERSION_SECTION, page, requestToken);
    while (true) {
      try {
        const html = await fetchHtml(url, signal);
        const fetchedAt = tm24PerfNow();
        const shard = parseIndexVersionShard(html, page);
        tm24Perf.versionShards[page - 1] = {
          page,
          startedAt,
          fetchedAt,
          parsedAt: tm24PerfNow(),
        };
        return shard;
      } catch (error) {
        if (isAbort(error) || signal?.aborted || attempt >= SHARD_RETRY_LIMIT) throw error;
        attempt += 1;
      }
    }
  }

  async function pooledPages(pages, worker, signal) {
    let cursor = 0;
    const count = Math.min(INDEX_CONCURRENCY, pages.length);
    const workers = Array.from({ length: count }, async () => {
      while (cursor < pages.length) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const current = pages[cursor];
        cursor += 1;
        await worker(current);
      }
    });
    await Promise.all(workers);
  }

  async function fetchCompleteIndex(source, signal) {
    return withDeadline(signal, COMPLETE_INDEX_DEADLINE, 'index_deadline', async (deadlineSignal) => {
      tm24Perf.indexLoadStartedMs = tm24PerfNow();
      const requestToken = `full-${now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      const shards = new Map();
      const expected = safeInteger(source.expected);
      const predictedPages = expected > 0 ? Math.ceil(expected / INDEX_PAGE_SIZE) : 0;
      let first = null;

      if (predictedPages > 0) {
        const requestedPages = Array.from({ length: predictedPages }, (_, index) => index + 1);
        await pooledPages(requestedPages, async (page) => {
          const shard = await fetchIndexShard(source, page, deadlineSignal, requestToken);
          shards.set(page, shard);
          if (page === 1) tm24Perf.firstIndexShardReadyMs = tm24PerfNow();
        }, deadlineSignal);
        first = shards.get(1);
        if (
          !first
          || first.expected !== expected
          || first.pages !== predictedPages
          || first.pageSize !== INDEX_PAGE_SIZE
        ) throw sourceError('Unexpected index pagination contract', 'pagination_contract_mismatch');
      } else {
        first = await fetchIndexShard(source, 1, deadlineSignal, requestToken);
        tm24Perf.firstIndexShardReadyMs = tm24PerfNow();
        shards.set(1, first);
        const remaining = [];
        for (let page = 2; page <= first.pages; page += 1) remaining.push(page);
        await pooledPages(remaining, async (page) => {
          shards.set(page, await fetchIndexShard(source, page, deadlineSignal, requestToken));
        }, deadlineSignal);
      }

      shards.forEach((shard) => {
        if (
          shard.pages !== first.pages
          || shard.expected !== first.expected
          || shard.pageSize !== first.pageSize
        ) throw sourceError('Collection changed while loading', 'catalog_changed');
      });
      tm24Perf.allIndexShardsReadyMs = tm24PerfNow();

      if (shards.size !== first.pages) {
        throw sourceError('Not every pagination shard loaded', 'incomplete_pages');
      }

      const products = [];
      const fingerprints = [];
      const identifiers = new Set();
      [...shards.keys()].sort((a, b) => a - b).forEach((page) => {
        const shard = shards.get(page);
        fingerprints.push(shard.fingerprint);
        shard.products.forEach((product) => {
          const id = String(product.id);
          if (identifiers.has(id)) throw sourceError('Duplicate product across shards', 'duplicate_product');
          identifiers.add(id);
          products.push(product);
        });
      });
      if (products.length !== first.expected) {
        throw sourceError(`Expected ${first.expected}, received ${products.length}`, 'incomplete_index');
      }

      const completedAt = now();
      tm24Perf.indexAssembledMs = tm24PerfNow();
      return Object.freeze({
        key: source.key,
        path: source.path,
        params: source.params.toString(),
        products: Object.freeze(products),
        expected: first.expected,
        pages: first.pages,
        pageSize: first.pageSize,
        fingerprints: Object.freeze(fingerprints),
        fetchedAt: completedAt,
        validatedAt: completedAt,
        complete: true,
      });
    });
  }

  async function fetchIndexVersion(source, cached, signal) {
    return withDeadline(signal, VERSION_VALIDATION_DEADLINE, 'version_deadline', async (deadlineSignal) => {
      tm24Perf.versionValidationStartedMs = tm24PerfNow();
      const requestToken = `version-${now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      const pages = Array.from({ length: safeInteger(cached.pages) }, (_, index) => index + 1);
      const shards = new Map();
      await pooledPages(pages, async (page) => {
        shards.set(page, await fetchIndexVersionShard(source, page, deadlineSignal, requestToken));
      }, deadlineSignal);
      const first = shards.get(1);
      if (!first || shards.size !== pages.length) {
        throw sourceError('Incomplete index version', 'incomplete_version');
      }
      const fingerprints = pages.map((page) => shards.get(page)?.fingerprint || '');
      tm24Perf.versionValidationReadyMs = tm24PerfNow();
      publishTm24Perf();
      return Object.freeze({
        expected: first.expected,
        pages: first.pages,
        pageSize: first.pageSize,
        fingerprints: Object.freeze(fingerprints),
      });
    });
  }

  function indexVersionMatches(dataset, version) {
    return Boolean(
      dataset?.complete
      && safeInteger(dataset.expected) === safeInteger(version?.expected)
      && safeInteger(dataset.pages) === safeInteger(version?.pages)
      && safeInteger(dataset.pageSize) === safeInteger(version?.pageSize)
      && validFingerprints(dataset.fingerprints, safeInteger(dataset.pages))
      && validFingerprints(version?.fingerprints, safeInteger(dataset.pages))
      && dataset.fingerprints.every((fingerprint, index) => fingerprint === version.fingerprints[index])
    );
  }

  function indexVersionSignature(dataset) {
    return [
      safeInteger(dataset?.expected),
      safeInteger(dataset?.pages),
      safeInteger(dataset?.pageSize),
      ...(Array.isArray(dataset?.fingerprints) ? dataset.fingerprints : []),
    ].join('|');
  }

  function dispatchIndexEvent(name, detail) {
    if (typeof window.dispatchEvent !== 'function' || typeof window.CustomEvent !== 'function') return;
    try {
      window.dispatchEvent(new window.CustomEvent(name, { detail }));
    } catch (_error) {
      // A background cache validation must never surface as an unhandled error.
    }
  }

  function scheduleCachedIndexValidation(source, cached) {
    if (
      !cached?.complete
      || indexValidatedThisDocument.has(source.key)
      || indexValidationAttempted.has(source.key)
      || indexValidationInflight.has(source.key)
    ) return;
    indexValidationAttempted.add(source.key);
    const controller = new AbortController();
    const validation = (async () => {
      let version = null;
      let validationError = null;
      try {
        version = await fetchIndexVersion(source, cached, controller.signal);
      } catch (error) {
        if (isAbort(error)) return;
        validationError = error;
      }

      if (version && indexVersionMatches(cached, version)) {
        indexValidatedThisDocument.add(source.key);
        return;
      }

      try {
        const fresh = await loadCompleteIndex(source, controller.signal, 0, true);
        if (indexVersionSignature(fresh) !== indexVersionSignature(cached)) {
          dispatchIndexEvent(INDEX_UPDATED_EVENT, { sourceKey: source.key, dataset: fresh });
        }
      } catch (error) {
        if (isAbort(error)) return;
        dispatchIndexEvent(INDEX_VALIDATION_ERROR_EVENT, {
          sourceKey: source.key,
          errorCode: String(error?.code || validationError?.code || 'source_error'),
        });
      }
    })().finally(() => {
      if (indexValidationInflight.get(source.key)?.promise === validation) {
        indexValidationInflight.delete(source.key);
      }
    });
    indexValidationInflight.set(source.key, { controller, promise: validation });
  }

  function loadCompleteIndex(source, signal, _ttl, force = false) {
    const aborted = () => Promise.reject(new DOMException('Aborted', 'AbortError'));
    const attachOwner = (entry, ownerSignal) => {
      if (!ownerSignal) return true;
      if (ownerSignal.aborted) return false;
      if (entry.owners.has(ownerSignal)) return true;
      const release = () => {
        entry.owners.delete(ownerSignal);
        entry.ownerListeners.delete(ownerSignal);
        if (!entry.settled && entry.owners.size === 0) entry.controller.abort();
      };
      entry.owners.add(ownerSignal);
      entry.ownerListeners.set(ownerSignal, release);
      ownerSignal.addEventListener('abort', release, { once: true });
      return true;
    };
    if (signal?.aborted) return aborted();

    // The network request has its own controller and is shared by stable
    // section owners. Short-lived filter/search action controllers therefore
    // cannot cancel or duplicate the full paginated Finder transfer.
    const requestKey = `${source.key}|${force ? 'network' : 'reuse'}`;
    const currentRequest = indexInflight.get(requestKey);
    if (currentRequest) {
      if (!currentRequest.controller.signal.aborted && attachOwner(currentRequest, signal)) {
        return currentRequest.promise;
      }
      if (indexInflight.get(requestKey) === currentRequest) indexInflight.delete(requestKey);
    }

    const requestEntry = {
      controller: new AbortController(),
      owners: new Set(),
      ownerListeners: new Map(),
      settled: false,
      promise: null,
    };
    if (!attachOwner(requestEntry, signal)) return aborted();
    const request = (async () => {
      if (!force) {
        let cached = cacheRead(indexCache, source.key);
        if (!cached) cached = readSessionIndex(source);
        if (!cached) cached = await readPersistentIndex(source);
        if (cached?.complete) {
          cacheWrite(indexCache, source.key, cached, MAX_INDEX_CACHE_ENTRIES);
          scheduleCachedIndexValidation(source, cached);
          return cached;
        }
      }

      const dataset = await fetchCompleteIndex(source, requestEntry.controller.signal);
      if (requestEntry.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      indexValidatedThisDocument.add(source.key);
      cacheWrite(indexCache, source.key, dataset, MAX_INDEX_CACHE_ENTRIES);
      writeSessionIndex(dataset);
      await writePersistentIndex(dataset);
      return dataset;
    })()
      .then((dataset) => {
        cacheWrite(indexCache, source.key, dataset, MAX_INDEX_CACHE_ENTRIES);
        return dataset;
      })
      .finally(() => {
        requestEntry.settled = true;
        requestEntry.ownerListeners.forEach((listener, ownerSignal) => {
          ownerSignal.removeEventListener('abort', listener);
        });
        requestEntry.ownerListeners.clear();
        requestEntry.owners.clear();
        if (indexInflight.get(requestKey) === requestEntry) indexInflight.delete(requestKey);
      });
    requestEntry.promise = request;
    indexInflight.set(requestKey, requestEntry);
    return request;
  }

  function parseCardShard(html, requestedPage) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = documentFragment.querySelector('[data-tm24-search-card-shard]');
    if (!wrapper || safeInteger(wrapper.dataset.page) !== requestedPage) {
      throw sourceError('Missing card shard markup', 'invalid_card_markup');
    }
    const templates = new Map();
    wrapper.querySelectorAll('template[data-tm24-search-card-template]').forEach((template) => {
      const id = String(template.dataset.productId || '');
      if (id && !templates.has(id)) templates.set(id, template);
    });
    if (templates.size !== safeInteger(wrapper.dataset.pageItems)) {
      throw sourceError('Card shard item count mismatch', 'card_count_mismatch');
    }
    return templates;
  }

  function parseNativeShard(html, requestedPage) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = documentFragment.querySelector('[data-tm24-search-native-shard]');
    const payload = wrapper?.querySelector('[data-tm24-search-products]');
    if (!wrapper || !payload || wrapper.dataset.schemaVersion !== NATIVE_SCHEMA) {
      throw sourceError('Missing native product-search shard', 'invalid_native_markup');
    }
    const page = safeInteger(wrapper.dataset.page);
    const pages = safeInteger(wrapper.dataset.pages);
    const expected = safeInteger(wrapper.dataset.expectedCount);
    if (!page || page !== requestedPage || !pages || page > pages) {
      throw sourceError('Invalid native pagination metadata', 'native_pagination_mismatch');
    }
    let products;
    try {
      products = JSON.parse(payload.textContent || '[]');
    } catch (_) {
      throw sourceError('Invalid native search JSON', 'invalid_native_json');
    }
    if (!Array.isArray(products)) throw sourceError('Invalid native search products', 'invalid_native_products');
    return { page, pages, expected, products };
  }

  function nativeSearchUrl(query, page) {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const url = new URL(`${routeRoot.replace(/\/?$/, '/')}search`, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', NATIVE_SECTION);
    url.searchParams.set('page', String(page));
    previewParams().forEach((value, key) => url.searchParams.set(key, value));
    return url;
  }

  async function fetchCompleteNativeSearch(query, signal) {
    const shards = new Map();
    const first = parseNativeShard(await fetchHtml(nativeSearchUrl(query, 1), signal), 1);
    shards.set(1, first);
    const remaining = [];
    for (let page = 2; page <= first.pages; page += 1) remaining.push(page);
    await pooledPages(remaining, async (page) => {
      const shard = parseNativeShard(await fetchHtml(nativeSearchUrl(query, page), signal), page);
      if (shard.pages !== first.pages || shard.expected !== first.expected) {
        throw sourceError('Native results changed while loading', 'native_results_changed');
      }
      shards.set(page, shard);
    }, signal);

    const products = [];
    const identifiers = new Set();
    [...shards.keys()].sort((a, b) => a - b).forEach((page) => {
      shards.get(page).products.forEach((product) => {
        const id = String(product.id);
        if (identifiers.has(id)) return;
        identifiers.add(id);
        products.push(product);
      });
    });
    if (shards.size !== first.pages || products.length !== first.expected) {
      throw sourceError('Native product-search result set is incomplete', 'incomplete_native_search');
    }
    return products;
  }

  async function fetchNativeSignal(query, signal) {
    const first = parseNativeShard(await fetchHtml(nativeSearchUrl(query, 1), signal), 1);
    const identifiers = new Set();
    const products = first.products.filter((product) => {
      const id = productId(product);
      if (!id || identifiers.has(id)) return false;
      identifiers.add(id);
      return true;
    });
    return {
      products,
      complete: first.pages === 1 && products.length === first.expected,
    };
  }

  async function loadCardPage(product, signal, ttl) {
    const cardPage = safeInteger(product._tm24CardPage, 1);
    const key = `${product._tm24SourceKey}|cards|${cardPage}`;
    const cached = cacheRead(cardCache, key);
    if (cached && now() - cached.fetchedAt <= ttl) return cached.templates;

    const params = new URLSearchParams(product._tm24SourceParams || '');
    const url = sectionUrl(product._tm24SourcePath, params, CARD_SECTION, cardPage);
    const html = await fetchHtml(url, signal);
    const templates = parseCardShard(html, cardPage);
    cacheWrite(cardCache, key, { templates, fetchedAt: now() }, MAX_CARD_CACHE_ENTRIES);
    return templates;
  }

  function normalizedResults(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.results)) return raw.results;
    return [];
  }

  function productOf(result) {
    return result?.product || result?.item || result;
  }

  function productId(product) {
    return String(product?.id ?? product?.productId ?? product?.handle ?? '').split('/').pop();
  }

  function isAbort(error) {
    return error?.name === 'AbortError';
  }

  class UnifiedSearch {
    constructor(root) {
      this.root = root;
      this.surface = root.closest('.tm24-unified-finder') || root;
      this.id = root.id || `TM24UnifiedSearch-${Math.random().toString(36).slice(2)}`;
      this.form = root.querySelector('[data-tm24-search-form]');
      this.input = root.querySelector('[data-tm24-search-input]');
      this.clear = root.querySelector('[data-tm24-search-clear]');
      this.popover = root.querySelector('[data-tm24-search-popover]');
      this.suggestionList = root.querySelector('[data-tm24-search-suggestions]');
      this.live = root.querySelector('[data-tm24-search-live]');
      this.results = root.querySelector('[data-tm24-search-results]');
      this.summary = root.querySelector('[data-tm24-search-summary]');
      this.shown = root.querySelector('[data-tm24-search-shown]');
      this.grid = root.querySelector('[data-tm24-search-grid]');
      this.loading = root.querySelector('[data-tm24-search-loading]');
      this.error = root.querySelector('[data-tm24-search-error]');
      this.errorMessage = root.querySelector('[data-tm24-search-error-message]');
      this.retry = root.querySelector('[data-tm24-search-retry]');
      this.more = root.querySelector('[data-tm24-search-more]');
      this.resetFiltersButton = root.querySelector('[data-tm24-search-reset-filters]');
      this.diagnosis = root.querySelector('[data-tm24-search-diagnosis]');
      this.routing = root.querySelector('[data-tm24-search-routing]');
      this.routingHeading = root.querySelector('[data-tm24-search-routing-heading]');
      this.routingList = root.querySelector('[data-tm24-search-routing-list]');
      this.help = root.querySelector('[data-tm24-search-help]');
      this.finderLink = root.querySelector('[data-tm24-search-finder-link]');
      this.adviceLink = root.querySelector('[data-tm24-search-advice-link]');
      this.facetsHost = this.surface.querySelector('[data-tm24-search-facets-host]');
      this.facetsContent = this.facetsHost?.querySelector('[data-tm24-search-facets-content]') || null;
      this.facetsStatus = this.facetsHost?.querySelector('[data-tm24-search-facets-status]') || null;
      this.facetsRetry = this.facetsHost?.querySelector('[data-tm24-search-facets-retry]') || null;
      this.finderSortTemplate = root.querySelector('[data-tm24-finder-sort-template]');
      this.context = root.dataset.context === 'finder' ? 'finder' : 'collection';
      this.scopePath = root.dataset.scopePath;
      this.finderScopePath = root.dataset.finderScopePath || '';
      this.finderScopeHandle = root.dataset.finderScopeHandle || '';
      this.pageSize = Math.max(1, safeInteger(root.dataset.pageSize, 12));
      this.ttl = Math.max(30_000, safeInteger(root.dataset.cacheTtl, 120_000));
      this.cardTtl = Math.min(this.ttl, 30_000);
      this.serial = 0;
      this.suggestionSerial = 0;
      this.activeSuggestion = -1;
      this.suppressSuggestionsUntilInput = false;
      this.visible = this.pageSize;
      this.matches = [];
      this.lastQuery = '';
      this.appliedCorrection = '';
      this.lastDataset = null;
      this.vocabulary = null;
      this.vocabularyKey = '';
      this.events = new AbortController();
      this.scopeController = new AbortController();
      this.finderIndexController = new AbortController();
      this.cardController = null;
      this.routingController = null;
      this.finderFilters = parseFinderFilters();
      this.finderFilterCatalog = null;
      this.finderCatalogSourceVersion = '';
      this.finderCatalogFetchedAt = 0;
      this.finderScopeProducts = [];
      this.finderFilterProducts = [];
      this.finderFilterProductsReady = false;
      this.finderFilterProductsQuery = '';
      this.finderFilterProductsSourceVersion = '';
      this.finderFilterProductsFetchedAt = 0;
      this.finderFilterLoad = null;
      this.finderFacetSerial = 0;
      this.finderCommitFrame = 0;
      this.finderCommitTimer = 0;
      this.finderFacetCatalogSignature = '';
      this.finderFacetCounts = null;
      this.finderFacetCountsPending = false;
      this.finderUiState = {
        expanded: {
          manufacturer: this.finderFilters.manufacturer.length > 0,
          gateTypes: this.finderFilters.gateTypes.length > 0,
          productTypes: this.finderFilters.productTypes.length > 0,
        },
        revealed: {
          manufacturer: true,
          gateTypes: true,
          productTypes: false,
        },
        optionQueries: {
          manufacturer: '',
          gateTypes: '',
          productTypes: '',
        },
      };
      this.finderDrawerOpen = false;
      this.finderDrawerTrigger = null;
      this.finderDrawerBackground = [];
      this.finderMobileMedia = typeof matchMedia === 'function' ? matchMedia(FINDER_MOBILE_MEDIA) : null;
      this.finderUnfilteredQueryCount = null;
      this.searchTimer = 0;
      this.suggestionTimer = 0;
      this.historyScrollFrame = 0;
      this.restoreScrollFrame = 0;
      this.facetWatchTimer = 0;
      this.facetDomTimer = 0;
      this.facetSignature = this.currentFacetSignature();
      this.facetsObserver = null;
      this.nativeGrid = null;
      this.nativeState = null;
      this.nativeCountSelector = root.dataset.nativeCountSelector || '';
      this.nativeCountStates = new Map();
      this.finderQualifiedCount = 0;
      this.routingAllowed = new Set(String(root.dataset.routingAllowedHandles || '').split(/[|,\s]+/).filter(Boolean));
      this.routingAllowedIds = new Set(String(root.dataset.routingAllowedIds || '').split(/[|,\s]+/).filter(Boolean));
      this.routingExcluded = new Set(String(root.dataset.routingExcludedHandles || '').split(/[|,\s]+/).filter(Boolean));
      this.focusRevalidation = null;
      this.stableResultState = null;
      this.moreGuard = null;
      this.moreGuardTimer = 0;
      this.moreGuardResolve = null;
      this.destroyed = false;

      if (!this.form || !this.input || !this.results || !this.grid || !this.scopePath) return;
      if (this.context === 'finder' && this.facetsHost) this.results.before(this.facetsHost);
      this.bind();
      if (this.context === 'collection') this.observeFacetDom();
      this.syncFilterForms('');
      this.placeResults();
      this.restoreInitialState();
      this.loadOwnedFacets();
    }

    source(filtered) {
      const params = canonicalParams({ filtered });
      if (this.context === 'finder') {
        [...params.keys()].forEach((key) => {
          if (key.startsWith('filter.')) params.delete(key);
        });
      }
      const expected = safeInteger(this.root.dataset.scopeExpected);
      return {
        path: this.scopePath,
        params,
        expected,
        key: sourceKey(this.scopePath, params, expected),
      };
    }

    async datasets(force = false) {
      const filteredSource = this.source(true);
      const baseSource = this.source(false);
      const filtered = hasActiveFilters(filteredSource.params);
      const signal = this.context === 'finder'
        ? this.finderIndexController.signal
        : this.scopeController.signal;
      const basePromise = loadCompleteIndex(baseSource, signal, this.ttl, force);
      const activePromise = filtered
        ? loadCompleteIndex(filteredSource, signal, this.ttl, force)
        : basePromise;
      const [base, active] = await Promise.all([basePromise, activePromise]);
      if (!base.complete || !active.complete) throw sourceError('Scope is not complete', 'incomplete_index');
      return { base, active, filtered };
    }

    bind() {
      const signal = this.events.signal;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const selected = this.suggestionItems()[this.activeSuggestion];
        if (selected) {
          selected.click();
          return;
        }
        this.closeSuggestions();
        this.execute(this.input.value, { historyMode: 'push' });
      }, { signal });

      this.input.addEventListener('input', () => {
        // A chosen suggestion must stay closed until the customer actually
        // changes the query again. Programmatic focus after selection is not
        // a new query and must not reopen the popover.
        this.suppressSuggestionsUntilInput = false;
        this.serial += 1;
        this.suggestionSerial += 1;
        this.cardController?.abort();
        this.routingController?.abort();
        this.settleAbortedExecution();
        this.input.value = this.input.value.slice(0, MAX_QUERY_LENGTH);
        this.clear.hidden = !this.input.value;
        if (!this.input.value.trim()) this.closeSuggestions();
        clearTimeout(this.searchTimer);
        clearTimeout(this.suggestionTimer);
        this.suggestionTimer = window.setTimeout(() => this.updateSuggestions(), 80);
        const historyMode = this.lastQuery ? 'replace' : 'push';
        this.searchTimer = window.setTimeout(() => this.execute(this.input.value, {
          historyMode,
          keepSuggestions: true,
        }), 180);
      }, { signal });
      this.input.addEventListener('focus', () => {
        const cached = indexCache.get(this.source(false).key);
        const cacheIsFresh = cached?.complete && now() - safeInteger(cached.fetchedAt) <= this.ttl;
        const finderLoadIsActive = this.context === 'finder' && this.finderFilterLoad;
        if (!cacheIsFresh && !this.focusRevalidation && !finderLoadIsActive) {
          const revalidation = this.datasets(false);
          revalidation.catch(() => {});
          this.focusRevalidation = revalidation;
          revalidation.then(
            () => {
              if (this.focusRevalidation === revalidation) this.focusRevalidation = null;
            },
            () => {
              if (this.focusRevalidation === revalidation) this.focusRevalidation = null;
            },
          );
          if (this.context === 'finder') {
            const focusSerial = this.serial;
            revalidation.then((sets) => {
              if (this.destroyed || focusSerial !== this.serial) return;
              const changed = this.adoptFinderCatalogDataset(sets.base);
              if (!changed || this.input.value.trim() || hasFinderFilters(this.finderFilters)) return;
              this.setFinderFilterProducts(sets.base.products, '', sets.base);
              this.renderFinderFacets(sets.base.products);
            }).catch(() => {});
          }
        }
        if (!this.suppressSuggestionsUntilInput) this.updateSuggestions();
      }, { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

      this.surface.addEventListener('submit', (event) => {
        const form = event.target?.closest?.('[data-tm24-search-facets]');
        if (!form || !this.surface.contains(form)) return;
        event.preventDefault();
        if (this.context === 'finder') return;
        this.applyOwnedFacetForm(form);
      }, { signal });
      this.surface.addEventListener('change', (event) => {
        const form = event.target?.closest?.('[data-tm24-search-facets]');
        if (!form || !this.surface.contains(form)) return;
        if (this.context === 'finder') {
          const filterControl = event.target?.closest?.('[data-tm24-finder-filter-axis]');
          if (filterControl) {
            this.applyFinderFilterControl(filterControl);
            return;
          }
          if (event.target?.matches?.('[name="sort_by"]')) {
            this.commitFinderFilters('push', event.target.value);
          }
          return;
        }
        this.applyOwnedFacetForm(form);
      }, { signal });
      this.surface.addEventListener('input', (event) => {
        const input = event.target?.closest?.('[data-tm24-finder-option-search]');
        if (this.context !== 'finder' || !input || !this.surface.contains(input)) return;
        const axis = FINDER_FILTER_AXES.find((item) => item.key === input.dataset.tm24FinderOptionSearch);
        if (!axis) return;
        this.finderUiState.optionQueries[axis.key] = String(input.value || '').slice(0, MAX_QUERY_LENGTH);
        this.syncFinderOptionSearch(axis.key, input);
      }, { signal });
      this.surface.addEventListener('click', (event) => {
        const reset = event.target?.closest?.('[data-tm24-search-facet-reset]');
        if (!reset || !this.surface.contains(reset)) return;
        event.preventDefault();
        this.clearOwnedFacets();
      }, { signal });
      this.surface.addEventListener('click', (event) => {
        const remove = event.target?.closest?.('[data-tm24-finder-filter-remove]');
        if (!remove || !this.surface.contains(remove)) return;
        event.preventDefault();
        const axis = FINDER_FILTER_AXES.find((item) => item.key === remove.dataset.tm24FinderFilterAxis);
        const value = normalizeFilterValue(remove.dataset.tm24FinderFilterValue);
        if (!axis || !value) return;
        this.finderFilters[axis.key] = this.finderFilters[axis.key].filter((item) => item !== value);
        this.commitFinderFilters('push');
      }, { signal });
      this.surface.addEventListener('click', (event) => {
        if (this.context !== 'finder') return;
        const open = event.target?.closest?.('[data-tm24-finder-drawer-open]');
        if (open && this.surface.contains(open)) {
          event.preventDefault();
          this.closeSuggestions();
          this.openFinderDrawer(open);
          return;
        }
        const close = event.target?.closest?.('[data-tm24-finder-drawer-close], [data-tm24-finder-drawer-backdrop], [data-tm24-finder-drawer-apply]');
        if (close && this.surface.contains(close)) {
          event.preventDefault();
          this.closeFinderDrawer();
          return;
        }
        const toggle = event.target?.closest?.('[data-tm24-finder-group-toggle]');
        if (toggle && this.surface.contains(toggle)) {
          event.preventDefault();
          this.closeSuggestions();
          this.toggleFinderGroup(toggle.dataset.tm24FinderGroupToggle);
          return;
        }
        const moreOptions = event.target?.closest?.('[data-tm24-finder-options-more]');
        if (moreOptions && this.surface.contains(moreOptions)) {
          event.preventDefault();
          this.toggleFinderOptions(moreOptions.dataset.tm24FinderOptionsMore);
          return;
        }
        const clearOptions = event.target?.closest?.('[data-tm24-finder-option-search-clear]');
        if (clearOptions && this.surface.contains(clearOptions)) {
          event.preventDefault();
          this.clearFinderOptionSearch(clearOptions.dataset.tm24FinderOptionSearchClear);
        }
      }, { signal });
      this.facetsRetry?.addEventListener('click', () => this.loadOwnedFacets(true), { signal });

      this.clear.addEventListener('click', () => {
        this.suppressSuggestionsUntilInput = false;
        this.input.value = '';
        this.clear.hidden = true;
        if (this.context === 'finder' && hasFinderFilters(this.finderFilters)) {
          this.execute('', { historyMode: 'push' });
        } else {
          this.reset({ historyMode: 'push' });
        }
        this.input.focus();
      }, { signal });
      this.more.addEventListener('click', (event) => this.showMore(event), { signal });
      this.retry.addEventListener('click', () => this.retrySearch(), { signal });
      this.resetFiltersButton.addEventListener('click', () => this.resetFilters(), { signal });
      this.suggestionList.addEventListener('click', (event) => {
        const button = event.target.closest('[data-tm24-search-term]');
        if (!button || !this.suggestionList.contains(button)) return;
        this.selectSuggestion(button);
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.root.contains(event.target)) this.closeSuggestions();
      }, { signal });
      document.addEventListener('keydown', (event) => this.onFinderDrawerKeydown(event), { signal });
      window.addEventListener('popstate', () => this.onPopState(), { signal });
      window.addEventListener(INDEX_UPDATED_EVENT, (event) => this.onIndexUpdated(event.detail), { signal });
      window.addEventListener(INDEX_VALIDATION_ERROR_EVENT, (event) => this.onIndexValidationError(event.detail), { signal });
      window.addEventListener('pagehide', () => {
        this.captureScroll();
        if (this.context === 'finder') this.finderIndexController.abort();
      }, { signal });
      window.addEventListener('pageshow', (event) => {
        if (
          this.context !== 'finder'
          || !event.persisted
          || this.destroyed
          || !this.finderIndexController.signal.aborted
        ) return;
        this.finderIndexController = new AbortController();
        this.finderFilterLoad = null;
        this.loadOwnedFacets(true);
        const query = this.input.value.trim();
        if (query || hasFinderFilters(this.finderFilters)) {
          this.execute(query, { historyMode: 'none', visible: this.visible });
        }
      }, { signal });
      window.addEventListener('resize', () => {
        this.positionPopover();
        if (this.finderDrawerOpen && this.finderMobileMedia && !this.finderMobileMedia.matches) {
          this.closeFinderDrawer({ returnFocus: false });
        }
      }, { signal, passive: true });
      window.addEventListener('scroll', () => {
        this.positionPopover();
        this.scheduleScrollCapture();
      }, { signal, passive: true });
      document.addEventListener('shopify:section:load', () => {
        this.syncFilterForms(this.input.value);
        this.loadOwnedFacets();
      }, { signal });
      if (this.context === 'collection') {
        document.addEventListener('change', (event) => {
          if (!event.target?.matches?.('[name^="filter."], [name="sort_by"]')) return;
          if (event.target.name === 'sort_by' && event.target.value === RELEVANCE_SORT) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.applyRelevanceSort();
            return;
          }
          this.watchFacetState();
        }, { signal, capture: true });
        document.addEventListener('click', (event) => {
          const control = event.target?.closest?.('a, button');
          if (!control) return;
          if (!control.closest('.facets, .active-facets, facet-filters-form, facets-form, [id^="Facet"], [id^="Filter"]')) return;
          this.watchFacetState();
        }, { signal, capture: true });
      }
    }

    restoreInitialState() {
      const state = this.readState();
      this.finderFilters = state.finderFilters;
      if (!state.query && !(this.context === 'finder' && hasFinderFilters(this.finderFilters))) return;
      this.input.value = state.query;
      this.appliedCorrection = state.correction;
      this.clear.hidden = !state.query;
      this.visible = state.visible;
      queueMicrotask(() => this.execute(state.query, {
        historyMode: 'none',
        visible: state.visible,
        restoreScroll: state.scrollY,
      }));
    }

    readState() {
      const params = new URLSearchParams(location.search);
      let parsed = {};
      try {
        parsed = core()?.parseState?.(location.search) || {};
      } catch (_) {
        parsed = {};
      }
      const own = history.state?.tm24UnifiedSearch;
      const query = String(
        parsed.query
        || parsed.q
        || (own?.id === this.id ? own.query : '')
        || params.get('tm24_q')
        || '',
      ).slice(0, MAX_QUERY_LENGTH);
      return {
        query,
        correction: String(parsed.correction || (own?.id === this.id ? own.correction : '') || '').slice(0, MAX_QUERY_LENGTH),
        visible: Math.max(this.pageSize, safeInteger(own?.visible ?? parsed.visible, this.pageSize)),
        scrollY: Number.isFinite(own?.scrollY) ? own.scrollY : null,
        finderFilters: this.context === 'finder'
          ? parseFinderFilters(location.search, parsed.filters)
          : emptyFinderFilters(),
      };
    }

    writeState(query, mode, previousEntryCaptured = false) {
      this.syncFilterForms(query);
      if (mode === 'none') return;
      if (mode === 'push' && !previousEntryCaptured) this.captureScroll(true);
      const url = new URL(location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('tm24_q');
      ['q', 'tm24_correction', 'tm24_visible', 'tm24_context', 'tm24_collection', FINDER_FILTERS_PARAM]
        .forEach((key) => url.searchParams.delete(key));
      if (query || (this.context === 'finder' && hasFinderFilters(this.finderFilters))) {
        let serialized = '';
        try {
          const state = core()?.createState?.({
            q: query,
            correction: this.appliedCorrection,
            visible: this.visible,
            context: this.context,
            collectionHandle: this.root.dataset.scopeHandle || '',
            filters: this.context === 'finder' ? compactFinderFilters(this.finderFilters) : {},
            sort: url.searchParams.get('sort_by') || 'relevance',
          });
          serialized = core()?.serializeState?.(state) || '';
        } catch (_) {
          serialized = '';
        }
        const coreParams = new URLSearchParams(serialized);
        const allowed = ['q', 'tm24_correction', 'tm24_visible', 'tm24_context', 'tm24_collection', FINDER_FILTERS_PARAM];
        allowed.forEach((key) => {
          if (coreParams.has(key)) url.searchParams.set(key, coreParams.get(key));
        });
        if (query && !url.searchParams.has('q')) url.searchParams.set('q', query);
        if (!query) url.searchParams.delete('q');
      }
      const state = {
        ...history.state,
        tm24UnifiedSearch: {
          id: this.id,
          query,
          correction: this.appliedCorrection,
          visible: this.visible,
          scrollY: window.scrollY,
          finderFilters: this.context === 'finder' ? this.finderFilters : undefined,
        },
      };
      history[mode === 'push' ? 'pushState' : 'replaceState'](state, '', url);
    }

    syncFilterForms(query) {
      const value = String(query || '').trim().slice(0, MAX_QUERY_LENGTH);
      document.querySelectorAll('form').forEach((form) => {
        if (!form.querySelector('[name^="filter."], [name="sort_by"]')) return;
        let input = form.querySelector('input[data-tm24-unified-query-state]');
        if (!value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'q';
          input.dataset.tm24UnifiedQueryState = '';
          form.append(input);
        }
        input.value = value;
      });
      this.syncSortControls(value);
      this.syncOwnedFacetForms();
    }

    syncSortControls(query) {
      this.ensureRelevanceSortControls();
      const requested = new URLSearchParams(location.search).get('sort_by');
      let desired = requested || '';
      document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        if (!select.dataset.tm24UnifiedOriginalSort) {
          select.dataset.tm24UnifiedOriginalSort = select.value;
        }
        if (!desired) desired = query ? RELEVANCE_SORT : select.dataset.tm24UnifiedOriginalSort;
        if ([...select.options].some((option) => option.value === desired)) select.value = desired;
      });
      if (!desired) return;
      document.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((radio) => {
        radio.checked = radio.value === desired;
      });
      document.querySelectorAll('.sort-selection').forEach((label) => {
        const container = label.closest('facet-sort, #SortByContainer') || document;
        const radio = [...container.querySelectorAll('input[type="radio"][name="sort_by"]')]
          .find((input) => input.value === desired);
        const optionLabel = radio?.id
          ? container.querySelector(`label[for="${CSS.escape(radio.id)}"]`)
          : null;
        if (optionLabel) label.textContent = optionLabel.textContent.trim();
      });
    }

    ensureRelevanceSortControls() {
      document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        if ([...select.options].some((option) => option.value === RELEVANCE_SORT)) return;
        const option = document.createElement('option');
        option.value = RELEVANCE_SORT;
        option.textContent = this.root.dataset.sortRelevance;
        select.prepend(option);
      });
      const groups = new Set();
      document.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((radio) => {
        const list = radio.closest('ul');
        if (list) groups.add(list);
      });
      groups.forEach((list, index) => {
        if (list.querySelector(`input[name="sort_by"][value="${RELEVANCE_SORT}"]`)) return;
        const item = document.createElement('li');
        item.className = 'opacity-0 relative';
        const input = document.createElement('input');
        input.className = 'sr-only';
        input.type = 'radio';
        input.name = 'sort_by';
        input.value = RELEVANCE_SORT;
        input.id = `${this.id}-relevance-sort-${index}`;
        const label = document.createElement('label');
        label.className = 'reversed-link text-xl';
        label.htmlFor = input.id;
        label.textContent = this.root.dataset.sortRelevance;
        item.append(input, label);
        list.prepend(item);
      });
    }

    onIndexUpdated(detail) {
      if (this.destroyed || !detail?.dataset || !detail?.sourceKey) return;
      const baseSource = this.source(false);
      const activeSource = this.source(true);
      if (detail.sourceKey !== baseSource.key && detail.sourceKey !== activeSource.key) return;
      if (this.context === 'finder' && detail.sourceKey === baseSource.key) {
        this.adoptFinderCatalogDataset(detail.dataset);
        const query = this.input.value.trim();
        if (!query && !hasFinderFilters(this.finderFilters)) {
          this.setFinderFilterProducts(detail.dataset.products, '', detail.dataset);
          this.renderFinderFacets(detail.dataset.products);
          return;
        }
      }
      if (!this.results.hidden || this.input.value.trim() || hasFinderFilters(this.finderFilters)) {
        this.execute(this.input.value, { historyMode: 'none', visible: this.visible });
      }
    }

    onIndexValidationError(detail) {
      if (this.destroyed || !detail?.sourceKey) return;
      const relevant = detail.sourceKey === this.source(false).key || detail.sourceKey === this.source(true).key;
      if (!relevant || this.context !== 'finder' || !this.finderFilterCatalog) return;
      this.facetsHost?.setAttribute('aria-busy', 'false');
      this.facetsContent?.querySelector('[data-tm24-search-facets]')?.setAttribute('aria-busy', 'false');
      if (this.facetsStatus) {
        this.facetsStatus.textContent = this.root.dataset.filterError || this.root.dataset.errorText;
        this.facetsStatus.hidden = false;
      }
      if (this.facetsRetry) {
        this.facetsRetry.disabled = false;
        this.facetsRetry.hidden = false;
      }
    }

    loadOwnedFacets(force = false) {
      if (this.context !== 'finder' || !this.facetsHost || !this.facetsContent) {
        return Promise.resolve();
      }
      if (this.finderIndexController.signal.aborted) this.finderIndexController = new AbortController();
      // A retry/focus action may join the same complete transfer, but must not
      // start a second forced 42-shard request while the first one is running.
      if (this.finderFilterLoad) return this.finderFilterLoad;
      if (!force && this.finderFilterCatalog) {
        this.renderFinderFacets(this.finderFilterProducts);
        return Promise.resolve();
      }
      const ownSerial = ++this.finderFacetSerial;
      tm24Perf.facetsStartedMs = tm24PerfNow();
      this.facetsHost.setAttribute('aria-busy', 'true');
      if (!this.finderFilterCatalog) this.renderFinderFacetLoading();
      if (this.facetsStatus) {
        this.facetsStatus.textContent = this.root.dataset.filterLoading || this.root.dataset.loadingText;
        this.facetsStatus.hidden = false;
      }
      if (this.facetsRetry) {
        this.facetsRetry.textContent = this.root.dataset.filterRetry || this.facetsRetry.textContent;
        this.facetsRetry.disabled = true;
        this.facetsRetry.hidden = true;
      }

      const request = (async () => {
        try {
          const dataset = await loadCompleteIndex(this.source(false), this.finderIndexController.signal, this.ttl, force);
          tm24Perf.indexDatasetReadyMs = tm24PerfNow();
          if (this.destroyed || ownSerial !== this.finderFacetSerial) return;
          this.adoptFinderCatalogDataset(dataset);
          tm24Perf.filterCatalogReadyMs = tm24PerfNow();
          this.setFinderFilterProducts(dataset.products, '', dataset);
          tm24Perf.filterProductsReadyMs = tm24PerfNow();
          this.renderFinderFacets(dataset.products);
          tm24Perf.firstUsableRenderMs = tm24PerfNow();
          publishTm24Perf(this.root);
          if (this.facetsStatus) this.facetsStatus.hidden = true;
          if (this.facetsRetry) {
            this.facetsRetry.disabled = false;
            this.facetsRetry.hidden = true;
          }
        } catch (error) {
          if (isAbort(error) || this.destroyed || ownSerial !== this.finderFacetSerial) return;
          if (!this.finderFilterCatalog) this.renderFinderFacetError();
          if (this.facetsStatus) {
            this.facetsStatus.textContent = this.root.dataset.filterError || this.root.dataset.errorText;
            this.facetsStatus.hidden = false;
          }
          if (this.facetsRetry) {
            this.facetsRetry.disabled = false;
            this.facetsRetry.hidden = false;
          }
          this.live.textContent = this.root.dataset.filterError || this.root.dataset.errorText;
        } finally {
          if (ownSerial === this.finderFacetSerial) {
            this.facetsHost.setAttribute('aria-busy', 'false');
            this.facetsContent?.querySelectorAll('[aria-busy="true"]').forEach((node) => {
              node.setAttribute('aria-busy', 'false');
            });
          }
          if (this.finderFilterLoad === request) this.finderFilterLoad = null;
        }
      })();
      this.finderFilterLoad = request;
      return request;
    }

    renderFinderFacetLoading() {
      if (!this.facetsContent) return;
      const shell = document.createElement('div');
      shell.className = 'tm24-finder-filters tm24-finder-filters--loading';
      shell.setAttribute('aria-busy', 'true');
      shell.setAttribute('aria-label', this.root.dataset.filterLoading || this.root.dataset.loadingText);
      const status = document.createElement('span');
      status.className = 'tm24-finder-filters__loading-label sr-only';
      status.textContent = this.root.dataset.filterLoading || this.root.dataset.loadingText;
      const skeleton = document.createElement('div');
      skeleton.className = 'tm24-finder-filters__skeleton';
      skeleton.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < 4; index += 1) {
        const item = document.createElement('span');
        item.className = 'tm24-finder-filters__skeleton-item';
        skeleton.append(item);
      }
      shell.append(status, skeleton);
      this.facetsContent.replaceChildren(shell);
    }

    renderFinderFacetError() {
      if (!this.facetsContent) return;
      const shell = document.createElement('div');
      shell.className = 'tm24-finder-filters tm24-finder-filters--error';
      shell.setAttribute('aria-busy', 'false');
      FINDER_FILTER_AXES.forEach((axis) => {
        const group = document.createElement('details');
        group.className = 'tm24-finder-filters__error-group';
        const summary = document.createElement('summary');
        summary.textContent = this.root.dataset[axis.labelData];
        const message = document.createElement('p');
        message.textContent = this.root.dataset.filterError || this.root.dataset.errorText;
        group.append(summary, message);
        shell.append(group);
      });
      this.facetsContent.replaceChildren(shell);
    }

    finderDatasetVersion(dataset) {
      return [dataset?.key, safeInteger(dataset?.fetchedAt), safeInteger(dataset?.expected), safeInteger(dataset?.pages)]
        .join('|');
    }

    setFinderFilterProducts(products, query = '', dataset = null) {
      this.finderFilterProducts = Array.isArray(products) ? products : [];
      this.finderFilterProductsQuery = String(query || '').trim();
      this.finderFilterProductsSourceVersion = dataset
        ? this.finderDatasetVersion(dataset)
        : this.finderCatalogSourceVersion;
      this.finderFilterProductsFetchedAt = safeInteger(dataset?.fetchedAt, this.finderCatalogFetchedAt);
      this.finderFilterProductsReady = Boolean(
        Array.isArray(products)
        && this.finderFilterProductsSourceVersion
        && this.finderFilterProductsFetchedAt > 0,
      );
    }

    adoptFinderCatalogDataset(dataset) {
      if (this.context !== 'finder' || !dataset?.complete || !Array.isArray(dataset.products)) return false;
      const version = this.finderDatasetVersion(dataset);
      if (this.finderFilterCatalog && version === this.finderCatalogSourceVersion) return false;
      this.finderFilterCatalog = buildFinderFilterCatalog(dataset.products);
      this.finderScopeProducts = dataset.products;
      this.finderCatalogSourceVersion = version;
      this.finderCatalogFetchedAt = safeInteger(dataset.fetchedAt);
      return true;
    }

    finderCatalogSignature() {
      return FINDER_FILTER_AXES.map((axis) => (
        `${axis.key}:${(this.finderFilterCatalog?.[axis.key] || []).map((option) => `${option.key}\u0000${option.label}`).join('|')}`
      )).join('::');
    }

    cloneFinderSort(suffix) {
      const sort = this.finderSortTemplate?.content?.firstElementChild?.cloneNode(true);
      if (!sort) return null;
      const select = sort.querySelector('[name="sort_by"]');
      const label = sort.querySelector('label');
      if (select) {
        select.id = `${this.id}-sort-${suffix}`;
        if (label) label.htmlFor = select.id;
        const requestedSort = new URLSearchParams(location.search).get('sort_by') || RELEVANCE_SORT;
        if ([...select.options].some((option) => option.value === requestedSort)) select.value = requestedSort;
      }
      return sort;
    }

    createFinderResetButton(extraClass = '') {
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = `tm24-finder-filters__reset ${extraClass}`.trim();
      reset.dataset.tm24SearchFacetReset = '';
      reset.textContent = this.root.dataset.resetAllFilters;
      return reset;
    }

    createFinderChipsHost() {
      const chips = document.createElement('div');
      chips.className = 'tm24-finder-filters__chips';
      chips.dataset.tm24FinderChips = '';
      chips.setAttribute('aria-label', this.root.dataset.activeFiltersLabel);
      return chips;
    }

    hydrateFinderGroupPanel(group, axis) {
      if (!group || group.dataset.tm24FinderHydrated === 'true') return;
      const panel = group.querySelector('[data-tm24-finder-group-panel]');
      if (!panel) return;
      const surfaceKey = group.dataset.tm24FinderGroupSurface;
      const options = this.finderFilterCatalog?.[axis.key] || [];
      const longGroup = options.length >= FINDER_LONG_GROUP_MINIMUM;
      if (longGroup) {
        const search = document.createElement('div');
        search.className = 'tm24-finder-filters__option-search';
        const searchId = `${this.id}-${surfaceKey}-${axis.key}-search`;
        const searchLabel = document.createElement('label');
        searchLabel.className = 'tm24-finder-filters__option-search-label';
        searchLabel.htmlFor = searchId;
        searchLabel.textContent = interpolate(this.root.dataset.filterOptionsSearchLabel, {
          group: this.root.dataset[axis.labelData],
        });
        const searchWrap = document.createElement('div');
        searchWrap.className = 'tm24-finder-filters__option-search-wrap';
        const searchInput = document.createElement('input');
        searchInput.id = searchId;
        searchInput.className = 'tm24-finder-filters__option-search-input';
        searchInput.type = 'search';
        searchInput.autocomplete = 'off';
        searchInput.value = this.finderUiState.optionQueries[axis.key];
        searchInput.placeholder = interpolate(this.root.dataset.filterOptionsSearchPlaceholder, {
          group: this.root.dataset[axis.labelData],
        });
        searchInput.dataset.tm24FinderOptionSearch = axis.key;
        const searchClear = document.createElement('button');
        searchClear.type = 'button';
        searchClear.className = 'tm24-finder-filters__option-search-clear';
        searchClear.dataset.tm24FinderOptionSearchClear = axis.key;
        searchClear.setAttribute('aria-label', interpolate(this.root.dataset.filterOptionsSearchClear, {
          group: this.root.dataset[axis.labelData],
        }));
        const searchClearGlyph = document.createElement('span');
        searchClearGlyph.setAttribute('aria-hidden', 'true');
        searchClearGlyph.textContent = '×';
        searchClear.append(searchClearGlyph);
        searchClear.hidden = !searchInput.value;
        searchWrap.append(searchInput, searchClear);
        search.append(searchLabel, searchWrap);
        panel.append(search);
      }

      const list = document.createElement('div');
      list.className = 'tm24-finder-filters__options';
      list.dataset.tm24FinderOptions = axis.key;
      options.forEach((option, optionIndex) => {
        const label = document.createElement('label');
        label.className = 'tm24-finder-filters__option';
        label.dataset.tm24FinderOption = option.key;
        label.dataset.tm24FinderOptionIndex = String(optionIndex);
        label.dataset.tm24FinderOptionSearchValue = normalizeFilterValue(option.label);
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = `tm24-filter-${axis.key}`;
        input.value = option.key;
        input.dataset.tm24FinderFilterAxis = axis.key;
        input.id = `${this.id}-${surfaceKey}-${axis.key}-${optionIndex}`;
        const text = document.createElement('span');
        text.className = 'tm24-finder-filters__option-label';
        text.dataset.tm24FilterLabel = '';
        text.textContent = option.label;
        const number = document.createElement('span');
        number.className = 'tm24-finder-filters__count';
        number.dataset.tm24FinderOptionCount = '';
        label.append(input, text, number);
        list.append(label);
      });
      panel.append(list);

      const empty = document.createElement('p');
      empty.className = 'tm24-finder-filters__empty-options';
      empty.dataset.tm24FinderEmptyOptions = axis.key;
      empty.textContent = this.root.dataset.filterNoOptionMatches;
      empty.hidden = true;
      panel.append(empty);

      if (axis.key === 'productTypes' && options.length > FINDER_PRODUCT_TYPE_INITIAL) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'tm24-finder-filters__more';
        more.dataset.tm24FinderOptionsMore = axis.key;
        panel.append(more);
      }
      group.dataset.tm24FinderHydrated = 'true';
    }

    buildFinderGroups(surfaceKey) {
      const groups = document.createElement('div');
      groups.className = 'tm24-finder-filters__groups';
      groups.dataset.tm24FinderGroups = surfaceKey;
      FINDER_FILTER_AXES.forEach((axis) => {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'tm24-finder-filters__group';
        fieldset.dataset.tm24FinderGroup = axis.key;
        fieldset.dataset.tm24FinderGroupSurface = surfaceKey;

        const legend = document.createElement('legend');
        legend.className = 'tm24-finder-filters__legend';
        const panelId = `${this.id}-${surfaceKey}-${axis.key}-panel`;
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'tm24-finder-filters__group-toggle';
        toggle.dataset.tm24FinderGroupToggle = axis.key;
        toggle.setAttribute('aria-controls', panelId);
        toggle.setAttribute('aria-expanded', 'false');
        const title = document.createElement('span');
        title.className = 'tm24-finder-filters__group-title';
        title.textContent = this.root.dataset[axis.labelData];
        const groupCount = document.createElement('span');
        groupCount.className = 'tm24-finder-filters__group-active-count';
        groupCount.dataset.tm24FinderGroupActiveCount = axis.key;
        const chevron = document.createElement('span');
        chevron.className = 'tm24-finder-filters__group-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        toggle.append(title, groupCount, chevron);
        legend.append(toggle);

        const panel = document.createElement('div');
        panel.id = panelId;
        panel.className = 'tm24-finder-filters__group-panel';
        panel.dataset.tm24FinderGroupPanel = axis.key;
        panel.hidden = true;
        fieldset.append(legend, panel);
        if (this.finderUiState.expanded[axis.key]) this.hydrateFinderGroupPanel(fieldset, axis);
        groups.append(fieldset);
      });
      return groups;
    }

    buildFinderFacetShell() {
      const form = document.createElement('form');
      form.className = 'tm24-finder-filters';
      form.dataset.tm24SearchFacets = '';
      form.setAttribute('aria-label', this.root.dataset.filtersLabel);

      const mobileBar = document.createElement('div');
      mobileBar.className = 'tm24-finder-filters__mobile-bar';
      const mobileOpen = document.createElement('button');
      mobileOpen.type = 'button';
      mobileOpen.className = 'tm24-finder-filters__mobile-open';
      mobileOpen.dataset.tm24FinderDrawerOpen = '';
      mobileOpen.setAttribute('aria-haspopup', 'dialog');
      const drawerId = `${this.id}-filter-dialog`;
      mobileOpen.setAttribute('aria-controls', drawerId);
      mobileOpen.setAttribute('aria-expanded', 'false');
      const mobileCount = document.createElement('span');
      mobileCount.className = 'tm24-finder-filters__active-count';
      mobileCount.dataset.tm24FinderMobileCount = '';
      mobileOpen.append(document.createTextNode(this.root.dataset.filterButton), mobileCount);
      mobileBar.append(mobileOpen);
      form.append(mobileBar);

      const desktop = document.createElement('div');
      desktop.className = 'tm24-finder-filters__desktop';
      const desktopPanelId = `${this.id}-desktop-filter-panel`;
      const desktopHeading = document.createElement('div');
      desktopHeading.className = 'tm24-finder-filters__heading-row';
      const desktopHeadingLabel = document.createElement('p');
      desktopHeadingLabel.className = 'tm24-finder-filters__heading';
      desktopHeadingLabel.textContent = this.root.dataset.filterButton;
      const activeCount = document.createElement('span');
      activeCount.className = 'tm24-finder-filters__active-count';
      activeCount.dataset.tm24FinderActiveCount = '';
      desktopHeadingLabel.append(document.createTextNode(' '), activeCount);
      const desktopPanel = document.createElement('div');
      desktopPanel.id = desktopPanelId;
      desktopPanel.className = 'tm24-finder-filters__desktop-panel';
      desktopPanel.dataset.tm24FinderDesktopPanel = '';
      const desktopSort = this.cloneFinderSort('desktop');
      desktopHeading.append(desktopHeadingLabel);
      if (desktopSort) desktopHeading.append(desktopSort);
      desktopPanel.append(desktopHeading, this.buildFinderGroups('desktop'));
      desktop.append(desktopPanel);
      form.append(desktop);

      const resultTopline = document.createElement('div');
      resultTopline.className = 'tm24-finder-filters__result-topline';
      const resultSummary = document.createElement('p');
      resultSummary.className = 'tm24-finder-filters__result-summary';
      resultSummary.dataset.tm24FinderResultSummary = '';
      const activeSummary = document.createElement('p');
      activeSummary.className = 'tm24-finder-filters__status';
      activeSummary.dataset.tm24FinderActiveSummary = '';
      resultTopline.append(resultSummary, activeSummary);
      form.append(resultTopline);

      const actions = document.createElement('div');
      actions.className = 'tm24-finder-filters__actions';
      actions.append(this.createFinderChipsHost(), this.createFinderResetButton());
      form.append(actions);

      const backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'tm24-finder-filters__backdrop';
      backdrop.dataset.tm24FinderDrawerBackdrop = '';
      backdrop.tabIndex = -1;
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.hidden = true;
      form.append(backdrop);

      const dialog = document.createElement('div');
      dialog.className = 'tm24-finder-filters__dialog';
      dialog.dataset.tm24FinderDrawer = '';
      dialog.id = drawerId;
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', `${this.id}-filter-dialog-title`);
      dialog.hidden = true;
      const dialogHeader = document.createElement('div');
      dialogHeader.className = 'tm24-finder-filters__dialog-header';
      const dialogTitle = document.createElement('p');
      dialogTitle.id = `${this.id}-filter-dialog-title`;
      dialogTitle.className = 'tm24-finder-filters__dialog-title';
      dialogTitle.textContent = this.root.dataset.filterDrawerTitle;
      const dialogClose = document.createElement('button');
      dialogClose.type = 'button';
      dialogClose.className = 'tm24-finder-filters__dialog-close';
      dialogClose.dataset.tm24FinderDrawerClose = '';
      dialogClose.setAttribute('aria-label', this.root.dataset.filterDrawerClose);
      const dialogCloseGlyph = document.createElement('span');
      dialogCloseGlyph.setAttribute('aria-hidden', 'true');
      dialogCloseGlyph.textContent = '×';
      dialogClose.append(dialogCloseGlyph);
      dialogHeader.append(dialogTitle, dialogClose);

      const dialogBody = document.createElement('div');
      dialogBody.className = 'tm24-finder-filters__dialog-body';
      const drawerSort = this.cloneFinderSort('drawer');
      if (drawerSort) dialogBody.append(drawerSort);
      const dialogActions = document.createElement('div');
      dialogActions.className = 'tm24-finder-filters__actions';
      dialogActions.append(this.createFinderChipsHost(), this.createFinderResetButton());
      dialogBody.append(dialogActions);

      const dialogFooter = document.createElement('div');
      dialogFooter.className = 'tm24-finder-filters__dialog-footer';
      const dialogFooterActions = document.createElement('div');
      dialogFooterActions.className = 'tm24-finder-filters__dialog-actions';
      dialogFooterActions.append(this.createFinderResetButton());
      const apply = document.createElement('button');
      apply.type = 'button';
      apply.className = 'tm24-finder-filters__dialog-apply';
      apply.dataset.tm24FinderDrawerApply = '';
      const drawerCount = document.createElement('span');
      drawerCount.className = 'tm24-finder-filters__drawer-count';
      drawerCount.dataset.tm24FinderDrawerCount = '';
      apply.append(drawerCount);
      dialogFooterActions.append(apply);
      const dialogStatus = document.createElement('p');
      dialogStatus.className = 'tm24-finder-filters__status sr-only';
      dialogStatus.dataset.tm24FinderDialogStatus = '';
      dialogStatus.setAttribute('role', 'status');
      dialogStatus.setAttribute('aria-live', 'polite');
      dialogFooter.append(dialogFooterActions, dialogStatus);
      dialog.append(dialogHeader, dialogBody, dialogFooter);
      form.append(dialog);
      return form;
    }

    activeFinderFilterCount() {
      return FINDER_FILTER_AXES.reduce((count, axis) => count + (this.finderFilters[axis.key] || []).length, 0);
    }

    activeFinderFilterText(count = this.activeFinderFilterCount()) {
      const template = count === 0
        ? this.root.dataset.activeFilterCountZero
        : (count === 1 ? this.root.dataset.activeFilterCountOne : this.root.dataset.activeFilterCountOther);
      return interpolate(template, { count });
    }

    finderCurrentResultCount(candidateProducts) {
      const seen = new Set();
      let total = 0;
      (candidateProducts || []).forEach((product) => {
        if (!productMatchesFinderFilters(product, this.finderFilters)) return;
        const id = productId(product);
        const identity = id || product;
        if (seen.has(identity)) return;
        seen.add(identity);
        total += 1;
      });
      return total;
    }

    finderResultSummaryText(total, rawQuery = this.input.value) {
      const query = String(rawQuery || '').trim();
      let template = '';
      if (total === 0) {
        if (query) {
          // A combined zero state is only truthful when the same query has
          // matches before TM24 Finder filters are applied. Otherwise the
          // query itself is the reason for zero results.
          template = hasFinderFilters(this.finderFilters) && this.finderUnfilteredQueryCount > 0
            ? this.root.dataset.noFilteredQueryResults
            : this.root.dataset.noResultsText;
        }
        else template = this.root.dataset.noFilterResults;
        return interpolate(template, { count: total, query });
      }
      template = query
        ? (total === 1 ? this.root.dataset.resultsOne : this.root.dataset.resultsOther)
        : (total === 1 ? this.root.dataset.filterResultsOne : this.root.dataset.filterResultsOther);
      return interpolate(template, { count: total, query });
    }

    renderFinderChips(host) {
      const existing = new Map([...host.querySelectorAll('[data-tm24-finder-filter-remove]')].map((button) => [
        `${button.dataset.tm24FinderFilterAxis}\u0000${button.dataset.tm24FinderFilterValue}`,
        button,
      ]));
      const fragment = document.createDocumentFragment();
      FINDER_FILTER_AXES.forEach((axis) => {
        (this.finderFilters[axis.key] || []).forEach((value) => {
          const option = this.finderFilterCatalog[axis.key]?.find((item) => item.key === value);
          const label = option?.label || value;
          const identity = `${axis.key}\u0000${value}`;
          const button = existing.get(identity) || document.createElement('button');
          existing.delete(identity);
          button.type = 'button';
          button.className = 'tm24-finder-filters__chip';
          button.dataset.tm24FinderFilterRemove = '';
          button.dataset.tm24FinderFilterAxis = axis.key;
          button.dataset.tm24FinderFilterValue = value;
          button.setAttribute('aria-label', interpolate(this.root.dataset.removeFilter, { value: label }));
          const chipLabel = button.querySelector('.tm24-finder-filters__chip-label') || document.createElement('span');
          chipLabel.className = 'tm24-finder-filters__chip-label';
          chipLabel.textContent = label;
          if (!chipLabel.parentElement) button.append(chipLabel);
          fragment.append(button);
        });
      });
      existing.forEach((button) => button.remove());
      host.replaceChildren(fragment);
    }

    syncFinderSelectionSummaryUi(
      form = this.facetsContent?.querySelector('[data-tm24-search-facets]'),
      syncSort = true,
    ) {
      if (!form || !this.finderFilterCatalog) return;
      const selectedCount = this.activeFinderFilterCount();
      const activeText = this.activeFinderFilterText(selectedCount);
      form.querySelectorAll('[data-tm24-finder-active-count], [data-tm24-finder-active-summary]')
        .forEach((node) => { node.textContent = activeText; });
      const mobileOpen = form.querySelector('[data-tm24-finder-drawer-open]');
      if (mobileOpen) {
        mobileOpen.firstChild.textContent = this.root.dataset.filterButton;
        mobileOpen.setAttribute('aria-label', selectedCount
          ? interpolate(this.root.dataset.filterButtonCount, { count: selectedCount })
          : this.root.dataset.filterButton);
        const mobileCount = mobileOpen.querySelector('[data-tm24-finder-mobile-count]');
        if (mobileCount) {
          mobileCount.textContent = String(selectedCount);
          mobileCount.hidden = selectedCount === 0;
        }
      }
      form.querySelectorAll('[data-tm24-finder-chips]').forEach((host) => this.renderFinderChips(host));
      form.querySelectorAll('[data-tm24-search-facet-reset]').forEach((button) => {
        button.hidden = selectedCount === 0;
      });
      if (syncSort) {
        const requestedSort = new URLSearchParams(location.search).get('sort_by') || RELEVANCE_SORT;
        form.querySelectorAll('[name="sort_by"]').forEach((select) => {
          if ([...select.options].some((option) => option.value === requestedSort)) select.value = requestedSort;
        });
      }

      FINDER_FILTER_AXES.forEach((axis) => {
        const selected = new Set(this.finderFilters[axis.key] || []);
        if (selected.size > 0) this.finderUiState.expanded[axis.key] = true;
          form.querySelectorAll(`[data-tm24-finder-group="${axis.key}"]`).forEach((group) => {
          const expanded = this.finderUiState.expanded[axis.key] === true;
          const toggle = group.querySelector('[data-tm24-finder-group-toggle]');
          if (toggle) {
            toggle.setAttribute('aria-expanded', String(expanded));
            toggle.setAttribute('aria-label', interpolate(
              expanded ? this.root.dataset.filterGroupCollapse : this.root.dataset.filterGroupExpand,
              { group: this.root.dataset[axis.labelData] },
            ));
          }
          const panel = group.querySelector('[data-tm24-finder-group-panel]');
          if (panel) panel.hidden = !expanded;
          group.dataset.collapsed = String(!expanded);
          group.querySelectorAll('[data-tm24-finder-group-active-count]').forEach((node) => {
            node.textContent = interpolate(this.root.dataset.filterGroupActive, { count: selected.size });
          });
        });
      });
    }

    syncFinderSelectionUi() {
      const form = this.facetsContent?.querySelector('[data-tm24-search-facets]');
      if (!form || !this.finderFilterCatalog) return;
      this.syncFinderSelectionSummaryUi(form);
      FINDER_FILTER_AXES.forEach((axis) => {
        const selected = new Set(this.finderFilters[axis.key] || []);
        form.querySelectorAll(`[data-tm24-finder-group="${axis.key}"]`).forEach((group) => {
          const list = group.querySelector('[data-tm24-finder-options]');
          if (!list) return;
          const rows = [...list.querySelectorAll('[data-tm24-finder-option]')];
          rows.forEach((row) => {
            const isSelected = selected.has(row.dataset.tm24FinderOption);
            row.dataset.active = String(isSelected);
            row.classList.toggle('is-active', isSelected);
            if (isSelected) row.dataset.tm24FinderHidden = 'false';
            const input = row.querySelector('[data-tm24-finder-filter-axis]');
            if (input) input.checked = isSelected;
          });
          rows.sort((left, right) => (
            Number(selected.has(right.dataset.tm24FinderOption)) - Number(selected.has(left.dataset.tm24FinderOption))
            || safeInteger(left.dataset.tm24FinderOptionIndex) - safeInteger(right.dataset.tm24FinderOptionIndex)
          ));
          rows.forEach((row) => list.append(row));
        });
      });
    }

    markFinderFacetCountsPending() {
      const form = this.facetsContent?.querySelector('[data-tm24-search-facets]');
      this.finderFacetCounts = null;
      this.finderFacetCountsPending = true;
      if (!form) return;
      form.querySelectorAll('[data-tm24-finder-option]').forEach((row) => {
        const input = row.querySelector('[data-tm24-finder-filter-axis]');
        row.classList.remove('is-disabled');
        row.setAttribute('aria-disabled', 'false');
        if (input) input.disabled = false;
        const number = row.querySelector('[data-tm24-finder-option-count]');
        if (number) {
          number.textContent = '';
          number.setAttribute('aria-hidden', 'true');
          number.removeAttribute('aria-label');
        }
      });
    }

    resetFinderDisclosureState() {
      FINDER_FILTER_AXES.forEach((axis) => {
        this.finderUiState.expanded[axis.key] = false;
      });
    }

    syncFinderDisclosureState() {
      FINDER_FILTER_AXES.forEach((axis) => {
        this.finderUiState.expanded[axis.key] = (this.finderFilters[axis.key] || []).length > 0;
      });
    }

    updateFinderImmediateFeedback(candidateProducts = this.finderFilterProducts) {
      if (!this.finderFilterCatalog) return;
      const counts = buildFinderFacetCounts(candidateProducts, this.finderFilters, this.finderFilterCatalog);
      // Keep every visible and accessible filter control canonical before the
      // asynchronous card/index revalidation starts. Announcements stay with
      // beginLoading()/finish(), so the immediate paint is not announced as a
      // completed search.
      this.updateFinderFacetUi(candidateProducts, counts, false, false);
    }

    updateFinderFacetUi(candidateProducts = this.finderFilterProducts, facetCounts = null, announce = true, syncSort = true) {
      const form = this.facetsContent?.querySelector('[data-tm24-search-facets]');
      if (!form || !this.finderFilterCatalog) return;
      const countsPending = this.finderFacetCountsPending && facetCounts == null;
      const counts = countsPending
        ? null
        : (facetCounts || buildFinderFacetCounts(candidateProducts, this.finderFilters, this.finderFilterCatalog));
      if (!countsPending) {
        this.finderFacetCounts = counts;
        this.finderFacetCountsPending = false;
      }
      const selectedCount = this.activeFinderFilterCount();
      const resultCount = this.finderCurrentResultCount(candidateProducts);
      const activeText = this.activeFinderFilterText(selectedCount);
      const resultText = this.finderResultSummaryText(resultCount);
      const statusText = interpolate(this.root.dataset.filterStatus, {
        results: resultText,
        active: activeText,
      });

      this.syncFinderSelectionSummaryUi(form, syncSort);

      if (!countsPending) form.querySelectorAll('[data-tm24-finder-result-summary]').forEach((node) => {
        node.textContent = resultText;
      });
      if (announce && !countsPending) {
        form.querySelectorAll('[data-tm24-finder-dialog-status]').forEach((node) => {
          node.textContent = statusText;
        });
        this.syncFinderLiveRegions(form);
      }
      if (!countsPending) form.querySelectorAll('[data-tm24-finder-drawer-count]').forEach((node) => {
        node.textContent = interpolate(this.root.dataset.filterShowResults, { count: resultCount });
      });

      FINDER_FILTER_AXES.forEach((axis) => {
        const selected = new Set(this.finderFilters[axis.key] || []);
        const options = this.finderFilterCatalog[axis.key] || [];
        const longGroup = options.length >= FINDER_LONG_GROUP_MINIMUM;
        const query = normalizeFilterValue(this.finderUiState.optionQueries[axis.key]);
        form.querySelectorAll(`[data-tm24-finder-group="${axis.key}"]`).forEach((group) => {
          const toggle = group.querySelector('[data-tm24-finder-group-toggle]');
          const panel = group.querySelector('[data-tm24-finder-group-panel]');
          const expanded = this.finderUiState.expanded[axis.key] === true;
          if (expanded) this.hydrateFinderGroupPanel(group, axis);
          if (toggle) {
            toggle.setAttribute('aria-expanded', String(expanded));
            toggle.setAttribute('aria-label', interpolate(
              expanded ? this.root.dataset.filterGroupCollapse : this.root.dataset.filterGroupExpand,
              { group: this.root.dataset[axis.labelData] },
            ));
          }
          if (panel) panel.hidden = !expanded;
          group.dataset.collapsed = String(!expanded);
          group.querySelectorAll('[data-tm24-finder-group-active-count]').forEach((node) => {
            node.textContent = interpolate(this.root.dataset.filterGroupActive, { count: selected.size });
          });
          group.querySelectorAll('[data-tm24-finder-option-search]').forEach((input) => {
            if (input.value !== this.finderUiState.optionQueries[axis.key]) input.value = this.finderUiState.optionQueries[axis.key];
          });
          group.querySelectorAll('[data-tm24-finder-option-search-clear]').forEach((button) => {
            button.hidden = !this.finderUiState.optionQueries[axis.key];
          });

          const list = group.querySelector('[data-tm24-finder-options]');
          if (!list) return;
          const rows = [...list.querySelectorAll('[data-tm24-finder-option]')];
          rows.sort((left, right) => (
            Number(selected.has(right.dataset.tm24FinderOption)) - Number(selected.has(left.dataset.tm24FinderOption))
            || safeInteger(left.dataset.tm24FinderOptionIndex) - safeInteger(right.dataset.tm24FinderOptionIndex)
          ));
          rows.forEach((row) => list.append(row));
          let eligibleUnselected = 0;
          let visibleRows = 0;
          rows.forEach((row) => {
            const key = row.dataset.tm24FinderOption;
            const isSelected = selected.has(key);
            const count = countsPending ? null : (counts[axis.key]?.get(key) || 0);
            const matchesQuery = isSelected || !query || row.dataset.tm24FinderOptionSearchValue.includes(query);
            const hiddenZero = !countsPending && longGroup && count === 0 && !isSelected;
            let hiddenDisclosure = false;
            if (axis.key === 'productTypes' && !query && !this.finderUiState.revealed[axis.key] && !isSelected && !hiddenZero) {
              const remainingSlots = Math.max(0, FINDER_PRODUCT_TYPE_INITIAL - selected.size);
              hiddenDisclosure = eligibleUnselected >= remainingSlots;
              eligibleUnselected += 1;
            }
            const hidden = !matchesQuery || hiddenZero || hiddenDisclosure;
            row.dataset.tm24FinderHidden = String(hidden);
            row.dataset.active = String(isSelected);
            row.classList.toggle('is-active', isSelected);
            const isDisabled = !countsPending && count === 0 && !isSelected;
            row.classList.toggle('is-disabled', isDisabled);
            row.setAttribute('aria-disabled', String(isDisabled));
            const input = row.querySelector('[data-tm24-finder-filter-axis]');
            if (input) {
              input.checked = isSelected;
              input.disabled = isDisabled;
            }
            const number = row.querySelector('[data-tm24-finder-option-count]');
            if (number) {
              number.textContent = countsPending ? '' : String(count);
              if (countsPending) {
                number.setAttribute('aria-hidden', 'true');
                number.removeAttribute('aria-label');
              } else {
                number.removeAttribute('aria-hidden');
                number.setAttribute('aria-label', interpolate(this.root.dataset.filterCount, { count }));
              }
            }
            if (!hidden) visibleRows += 1;
          });
          const empty = group.querySelector('[data-tm24-finder-empty-options]');
          if (empty) empty.hidden = visibleRows !== 0;
          const more = group.querySelector('[data-tm24-finder-options-more]');
          if (more) {
            const selectableRows = rows.filter((row) => {
              const key = row.dataset.tm24FinderOption;
              const count = countsPending ? null : (counts[axis.key]?.get(key) || 0);
              return countsPending || selected.has(key) || count > 0 || !longGroup;
            }).length;
            const canDisclose = !query && selectableRows > FINDER_PRODUCT_TYPE_INITIAL;
            more.hidden = !canDisclose;
            more.textContent = this.finderUiState.revealed[axis.key]
              ? this.root.dataset.filterShowLess
              : this.root.dataset.filterShowMore;
            more.setAttribute('aria-expanded', String(this.finderUiState.revealed[axis.key]));
          }
        });
      });
    }

    renderFinderFacets(candidateProducts = this.finderFilterProducts) {
      if (!this.finderFilterCatalog || !this.facetsContent) return;
      const signature = this.finderCatalogSignature();
      let form = this.facetsContent.querySelector('[data-tm24-search-facets]');
      if (!form || signature !== this.finderFacetCatalogSignature) {
        this.closeFinderDrawer({ returnFocus: false });
        form = this.buildFinderFacetShell();
        this.facetsContent.replaceChildren(form);
        this.finderFacetCatalogSignature = signature;
      }
      this.finderFacetCountsPending = false;
      this.updateFinderFacetUi(candidateProducts);
    }

    applyFinderFilterControl(control) {
      const axis = FINDER_FILTER_AXES.find((item) => item.key === control.dataset.tm24FinderFilterAxis);
      const value = normalizeFilterValue(control.value);
      if (!axis || !value) return;
      const selected = control.checked;
      const next = new Set(this.finderFilters[axis.key] || []);
      if (selected) next.add(value);
      else next.delete(value);
      const catalogOrder = new Map((this.finderFilterCatalog?.[axis.key] || []).map((option, index) => [option.key, index]));
      this.finderFilters[axis.key] = [...next].sort((left, right) => (
        (catalogOrder.get(left) ?? Number.MAX_SAFE_INTEGER) - (catalogOrder.get(right) ?? Number.MAX_SAFE_INTEGER)
        || left.localeCompare(right, locale())
      ));
      this.commitFinderFilters('push');
      // A selected row is moved ahead of the alphabetical options. Reset the
      // group's own scroll position so the active value does not jump outside
      // the visible list after that in-place reorder.
      if (selected) {
        this.facetsContent?.querySelectorAll(`[data-tm24-finder-group="${axis.key}"] [data-tm24-finder-options]`)
          .forEach((list) => { list.scrollTop = 0; });
      }
    }

    toggleFinderGroup(axisKey) {
      const axis = FINDER_FILTER_AXES.find((item) => item.key === axisKey);
      if (!axis) return;
      if (this.finderUiState.expanded[axis.key] && (this.finderFilters[axis.key] || []).length > 0) return;
      this.finderUiState.expanded[axis.key] = !this.finderUiState.expanded[axis.key];
      this.updateFinderFacetUi(this.finderFilterProducts, this.finderFacetCounts);
    }

    syncFinderLiveRegions(form = this.facetsContent?.querySelector('[data-tm24-search-facets]')) {
      if (!form) return;
      const drawerStatus = form.querySelector('[data-tm24-finder-dialog-status]');
      if (drawerStatus) drawerStatus.setAttribute('aria-live', this.finderDrawerOpen ? 'polite' : 'off');
      this.live.setAttribute('aria-live', this.finderDrawerOpen ? 'off' : 'polite');
    }

    toggleFinderOptions(axisKey) {
      if (axisKey !== 'productTypes') return;
      this.finderUiState.revealed[axisKey] = !this.finderUiState.revealed[axisKey];
      this.updateFinderFacetUi(this.finderFilterProducts, this.finderFacetCounts);
    }

    syncFinderOptionSearch(axisKey, activeInput = null) {
      const axis = FINDER_FILTER_AXES.find((item) => item.key === axisKey);
      if (!axis) return;
      this.updateFinderFacetUi(this.finderFilterProducts, this.finderFacetCounts);
      if (activeInput && document.activeElement !== activeInput) activeInput.focus({ preventScroll: true });
    }

    clearFinderOptionSearch(axisKey) {
      const axis = FINDER_FILTER_AXES.find((item) => item.key === axisKey);
      if (!axis) return;
      this.finderUiState.optionQueries[axis.key] = '';
      this.updateFinderFacetUi(this.finderFilterProducts, this.finderFacetCounts);
      const dialogSelector = this.finderDrawerOpen ? '[data-tm24-finder-drawer]' : '.tm24-finder-filters__desktop';
      this.facetsContent.querySelector(`${dialogSelector} [data-tm24-finder-option-search="${axis.key}"]`)?.focus();
    }

    finderFocusableElements(dialog) {
      return [...dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && !element.closest('[hidden]') && !element.closest('[data-tm24-finder-hidden="true"]'));
    }

    lockFinderDrawerBackground(dialog, backdrop) {
      this.finderDrawerBackground = [];
      let branch = dialog;
      while (branch?.parentElement) {
        const parent = branch.parentElement;
        [...parent.children].forEach((sibling) => {
          if (sibling === branch || sibling === backdrop || this.finderDrawerBackground.some((entry) => entry.element === sibling)) return;
          const entry = {
            element: sibling,
            inert: sibling.hasAttribute('inert'),
            ariaHidden: sibling.getAttribute('aria-hidden'),
          };
          this.finderDrawerBackground.push(entry);
          if ('inert' in sibling) sibling.inert = true;
          else sibling.setAttribute('aria-hidden', 'true');
        });
        if (parent === document.body) break;
        branch = parent;
      }
    }

    unlockFinderDrawerBackground() {
      this.finderDrawerBackground.forEach(({ element, inert, ariaHidden }) => {
        if (!element.isConnected) return;
        if ('inert' in element) element.inert = inert;
        if (ariaHidden == null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      this.finderDrawerBackground = [];
    }

    ensureFinderDrawerGroups(form) {
      const dialog = form?.querySelector('[data-tm24-finder-drawer]');
      const body = dialog?.querySelector('.tm24-finder-filters__dialog-body');
      if (!body || body.querySelector('[data-tm24-finder-groups="drawer"]')) return;
      body.append(this.buildFinderGroups('drawer'));
      this.updateFinderFacetUi(this.finderFilterProducts, this.finderFacetCounts, false);
    }

    openFinderDrawer(trigger) {
      if (this.context !== 'finder' || this.finderDrawerOpen) return;
      if (!this.finderMobileMedia?.matches) return;
      const form = this.facetsContent?.querySelector('[data-tm24-search-facets]');
      const dialog = form?.querySelector('[data-tm24-finder-drawer]');
      const backdrop = form?.querySelector('[data-tm24-finder-drawer-backdrop]');
      if (!dialog || !backdrop) return;
      this.ensureFinderDrawerGroups(form);
      this.finderDrawerOpen = true;
      this.finderDrawerTrigger = trigger;
      this.closeSuggestions();
      trigger.setAttribute('aria-expanded', 'true');
      backdrop.hidden = false;
      dialog.hidden = false;
      this.syncFinderLiveRegions(form);
      document.body.classList.add('tm24-finder-drawer-open');
      this.lockFinderDrawerBackground(dialog, backdrop);
      requestAnimationFrame(() => {
        const focusTarget = dialog.querySelector('[data-tm24-finder-drawer-close]') || this.finderFocusableElements(dialog)[0];
        focusTarget?.focus({ preventScroll: true });
      });
    }

    closeFinderDrawer({ returnFocus = true } = {}) {
      if (!this.finderDrawerOpen) return;
      const form = this.facetsContent?.querySelector('[data-tm24-search-facets]');
      const dialog = form?.querySelector('[data-tm24-finder-drawer]');
      const backdrop = form?.querySelector('[data-tm24-finder-drawer-backdrop]');
      if (dialog) dialog.hidden = true;
      if (backdrop) backdrop.hidden = true;
      this.finderDrawerOpen = false;
      this.syncFinderLiveRegions(form);
      this.unlockFinderDrawerBackground();
      if (!document.querySelector('.tm24-finder-filters__dialog:not([hidden])')) {
        document.body.classList.remove('tm24-finder-drawer-open');
      }
      const trigger = this.finderDrawerTrigger;
      trigger?.setAttribute('aria-expanded', 'false');
      this.finderDrawerTrigger = null;
      if (returnFocus && trigger?.isConnected && this.finderMobileMedia?.matches) {
        requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
      }
    }

    onFinderDrawerKeydown(event) {
      if (!this.finderDrawerOpen) return;
      const dialog = this.facetsContent?.querySelector('[data-tm24-finder-drawer]');
      if (!dialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeFinderDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.finderFocusableElements(dialog);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    syncOwnedFacetForms() {
      if (this.context === 'finder') {
        this.finderFilters = parseFinderFilters();
        if (this.finderFilterCatalog) this.syncFinderSelectionUi();
        return;
      }
      const params = new URLSearchParams(location.search);
      this.surface.querySelectorAll('[data-tm24-search-facets]').forEach((form) => {
        form.querySelectorAll('[name]').forEach((control) => {
          if (control.name === 'sort_by') {
            const value = params.get('sort_by') || RELEVANCE_SORT;
            if (control instanceof HTMLSelectElement) control.value = value;
            else if (control.type === 'radio') control.checked = control.value === value;
            return;
          }
          if (!control.name.startsWith('filter.')) return;
          if (control.type === 'checkbox' || control.type === 'radio') {
            control.checked = params.getAll(control.name).includes(control.value);
          } else {
            control.value = params.get(control.name) || '';
          }
        });
      });
    }

    applyOwnedFacetForm(form) {
      if (this.context === 'finder') {
        // Finder controls commit directly from the changed checkbox/select.
        // The desktop and drawer views mirror one canonical filter state.
        return;
      }
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') url.searchParams.delete(key);
      });
      const data = new FormData(form);
      data.forEach((rawValue, key) => {
        const value = String(rawValue || '').trim();
        if (!value || (!key.startsWith('filter.') && key !== 'sort_by')) return;
        if (key === 'sort_by' && value === RELEVANCE_SORT) return;
        url.searchParams.append(key, value);
      });
      const query = this.input.value.trim();
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');
      this.pushHistoryState(url);
      this.facetSignature = this.currentFacetSignature();
      this.syncOwnedFacetForms();
      this.visible = this.pageSize;
      this.scopeController.abort();
      this.scopeController = new AbortController();
      this.loadOwnedFacets();
      if (query) this.execute(query, { historyMode: 'none', visible: this.pageSize });
    }

    clearOwnedFacets() {
      if (this.context === 'finder') {
        this.finderFilters = emptyFinderFilters();
        this.resetFinderDisclosureState();
        this.commitFinderFilters('push');
        return;
      }
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') url.searchParams.delete(key);
      });
      if (this.input.value.trim()) url.searchParams.set('q', this.input.value.trim());
      this.pushHistoryState(url);
      this.facetSignature = this.currentFacetSignature();
      this.syncOwnedFacetForms();
      this.scopeController.abort();
      this.scopeController = new AbortController();
      this.loadOwnedFacets();
      if (this.input.value.trim()) this.execute(this.input.value, { historyMode: 'none', visible: this.pageSize });
    }

    cancelFinderCommit() {
      if (this.finderCommitFrame) cancelAnimationFrame(this.finderCommitFrame);
      if (this.finderCommitTimer) clearTimeout(this.finderCommitTimer);
      this.finderCommitFrame = 0;
      this.finderCommitTimer = 0;
    }

    invalidateActiveExecution() {
      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestionTimer);
      this.searchTimer = 0;
      this.suggestionTimer = 0;
      cancelAnimationFrame(this.restoreScrollFrame);
      this.restoreScrollFrame = 0;
      this.serial += 1;
      this.clearMoreInteraction();
      this.cardController?.abort();
      this.routingController?.abort();
      this.settleAbortedExecution();
      return this.serial;
    }

    commitFinderFilters(historyMode = 'push', sortValue) {
      this.cancelFinderCommit();
      const commitSerial = this.invalidateActiveExecution();
      this.closeSuggestions();
      const previousEntryCaptured = historyMode === 'push';
      if (previousEntryCaptured) this.captureScroll(true);
      const url = new URL(location.href);
      url.searchParams.delete('page');
      url.searchParams.delete(FINDER_FILTERS_PARAM);
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      });
      if (sortValue !== undefined) {
        if (sortValue && sortValue !== RELEVANCE_SORT) url.searchParams.set('sort_by', sortValue);
        else url.searchParams.delete('sort_by');
      }
      const serialized = serializeFinderFilters(this.finderFilters);
      if (serialized) url.searchParams.set(FINDER_FILTERS_PARAM, serialized);
      const query = this.input.value.trim();
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');
      // The last completed query owns a fully validated candidate set. Reuse
      // that set for the synchronous interaction response (controls, chips,
      // dependent counts and result count) while execute() revalidates the
      // index and hydrates matching cards in the background.
      const finderFeedbackAge = now() - this.finderFilterProductsFetchedAt;
      const preserveFinderFeedback = Boolean(
        this.finderFilterCatalog
        && this.finderFilterProductsReady
        && query === this.finderFilterProductsQuery
        && this.finderFilterProductsSourceVersion === this.finderCatalogSourceVersion
        && finderFeedbackAge >= 0
        && finderFeedbackAge <= this.ttl,
      );
      if (preserveFinderFeedback) {
        this.updateFinderImmediateFeedback(this.finderFilterProducts);
      } else {
        this.syncFinderSelectionUi();
        // No trustworthy candidate set is available for synchronous dependent
        // counts. Keep selected values removable, but fail closed for every
        // other option until the validated dataset renders fresh counts.
        this.markFinderFacetCountsPending();
      }
      if (historyMode === 'push') this.pushHistoryState(url, previousEntryCaptured);
      else history.replaceState({ ...history.state }, '', url);
      this.visible = this.pageSize;
      // Canonical URL and state commit synchronously. Only the expensive
      // revalidation is deferred, so an intervening query/popstate cannot
      // discard the user's just-confirmed filter or sort action.
      this.writeState(query, 'replace');
      const completeFilterCommit = () => {
        if (this.destroyed || commitSerial !== this.serial) return;
        if (query || hasFinderFilters(this.finderFilters)) {
          this.execute(query, {
            historyMode: 'none',
            visible: this.pageSize,
            preserveFinderFeedback,
          });
        } else {
          this.reset({ historyMode: 'none' });
        }
      };
      this.finderCommitFrame = requestAnimationFrame(() => {
        this.finderCommitFrame = 0;
        this.finderCommitTimer = window.setTimeout(() => {
          this.finderCommitTimer = 0;
          completeFilterCommit();
        }, 0);
      });
    }

    applyRelevanceSort() {
      const url = new URL(location.href);
      url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      const query = this.input.value.trim();
      if (query) url.searchParams.set('q', query);
      this.pushHistoryState(url);
      this.facetSignature = this.currentFacetSignature();
      this.syncSortControls(query);
      this.visible = this.pageSize;
      this.scopeController.abort();
      this.scopeController = new AbortController();
      this.loadOwnedFacets();
      if (query) this.execute(query, { historyMode: 'none', visible: this.pageSize });
    }

    observeFacetDom() {
      if (typeof MutationObserver !== 'function') return;
      const scope = this.root.closest('.collection') || this.root.parentElement || document.body;
      this.facetsObserver = new MutationObserver((records) => {
        const replaced = records.some((record) => [...record.addedNodes].some((node) => (
          node.nodeType === Node.ELEMENT_NODE
          && (node.matches?.('select[name="sort_by"], form[is="facet-form"], facets-topbar')
            || node.querySelector?.('select[name="sort_by"], form[is="facet-form"], facets-topbar')
            || (this.root.dataset.nativeSelector && node.matches?.(this.root.dataset.nativeSelector))
            || (this.root.dataset.nativeSelector && node.querySelector?.(this.root.dataset.nativeSelector))
            || (this.nativeCountSelector && node.matches?.(this.nativeCountSelector))
            || (this.nativeCountSelector && node.querySelector?.(this.nativeCountSelector)))
        )));
        if (!replaced) return;
        clearTimeout(this.facetDomTimer);
        this.facetDomTimer = window.setTimeout(() => {
          this.syncFilterForms(this.input.value);
          this.placeResults();
          if (this.input.value.trim() && !this.results.hidden) this.hideNativeGrid();
        }, 0);
      });
      this.facetsObserver.observe(scope, { childList: true, subtree: true });
    }

    currentFacetSignature() {
      const params = new URLSearchParams(location.search);
      const entries = [];
      params.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'sort_by') entries.push([key, value]);
      });
      entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => (
        leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue)
      ));
      return entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
    }

    watchFacetState() {
      clearTimeout(this.facetWatchTimer);
      clearTimeout(this.facetDomTimer);
      const startedAt = now();
      const previous = this.facetSignature;
      const check = () => {
        if (this.destroyed) return;
        const next = this.currentFacetSignature();
        if (next !== previous) {
          this.facetSignature = next;
          const query = this.input.value.trim();
          if (query) {
            this.visible = this.pageSize;
            this.scopeController.abort();
            this.scopeController = new AbortController();
            this.execute(query, { historyMode: 'none', visible: this.pageSize });
          }
          return;
        }
        if (now() - startedAt < 2500) {
          this.facetWatchTimer = window.setTimeout(check, 80);
        }
      };
      this.facetWatchTimer = window.setTimeout(check, 0);
    }

    captureScroll(ensureState = false) {
      const current = history.state?.tm24UnifiedSearch;
      if (current?.id !== this.id && !ensureState) return;
      const restored = this.readState();
      history.replaceState({
        ...history.state,
        tm24UnifiedSearch: {
          ...(current?.id === this.id ? current : {}),
          id: this.id,
          query: restored.query,
          correction: restored.correction,
          visible: this.visible,
          scrollY: window.scrollY,
          finderFilters: this.context === 'finder' ? restored.finderFilters : undefined,
        },
      }, '', location.href);
    }

    scheduleScrollCapture() {
      if (this.historyScrollFrame || this.destroyed) return;
      this.historyScrollFrame = requestAnimationFrame(() => {
        this.historyScrollFrame = 0;
        if (!this.destroyed) this.captureScroll();
      });
    }

    pushHistoryState(url, previousEntryCaptured = false) {
      // Persist the entry being left before pushState creates its successor.
      // This also initializes deep-linked entries that did not yet have an
      // owned state object when the first confirmed action occurs.
      if (!previousEntryCaptured) this.captureScroll(true);
      history.pushState({ ...history.state }, '', url);
    }

    placeResults() {
      if (this.context !== 'collection') return;
      const collectionScope = this.root.closest('.collection') || document;
      const slot = collectionScope.querySelector('[data-tm24-search-results-slot]');
      if (slot && this.results.parentElement !== slot) slot.append(this.results);
    }

    onPopState() {
      this.cancelFinderCommit();
      this.invalidateActiveExecution();
      cancelAnimationFrame(this.historyScrollFrame);
      this.historyScrollFrame = 0;
      cancelAnimationFrame(this.restoreScrollFrame);
      this.restoreScrollFrame = 0;
      this.facetSignature = this.currentFacetSignature();
      const state = this.readState();
      this.finderFilters = state.finderFilters;
      this.syncFinderDisclosureState();
      this.loadOwnedFacets();
      this.input.value = state.query;
      this.appliedCorrection = state.correction;
      this.clear.hidden = !state.query;
      if (!state.query && !(this.context === 'finder' && hasFinderFilters(this.finderFilters))) {
        this.reset({ historyMode: 'none' });
        if (state.scrollY != null) {
          const restoreSerial = this.serial;
          this.restoreScrollFrame = requestAnimationFrame(() => {
            this.restoreScrollFrame = 0;
            if (!this.destroyed && restoreSerial === this.serial) scrollTo(0, state.scrollY);
          });
        }
        return;
      }
      this.execute(state.query, {
        historyMode: 'none',
        visible: state.visible,
        restoreScroll: state.scrollY,
      });
    }

    suggestionItems() {
      return [...this.suggestionList.querySelectorAll('[data-tm24-search-term]')];
    }

    selectSuggestion(button) {
      if (!button || !this.suggestionList.contains(button)) return;
      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestionTimer);
      this.input.value = String(button.dataset.tm24SearchTerm || '').slice(0, MAX_QUERY_LENGTH);
      this.suppressSuggestionsUntilInput = true;
      this.clear.hidden = false;
      this.closeSuggestions();
      this.execute(this.input.value, { historyMode: 'push' });
      requestAnimationFrame(() => {
        if (!this.destroyed && this.input.isConnected) {
          this.input.focus({ preventScroll: true });
          const end = this.input.value.length;
          this.input.setSelectionRange?.(end, end);
        }
      });
    }

    onKeydown(event) {
      const items = this.suggestionItems();
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeSuggestion = (this.activeSuggestion + delta + items.length) % items.length;
        items.forEach((item, index) => {
          const active = index === this.activeSuggestion;
          item.setAttribute('aria-selected', String(active));
        });
        const active = items[this.activeSuggestion];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeSuggestion >= 0 && items[this.activeSuggestion]) {
        event.preventDefault();
        this.selectSuggestion(items[this.activeSuggestion]);
      } else if (
        (event.key === ' ' || event.key === 'Spacebar')
        && !this.finderMobileMedia?.matches
        && this.activeSuggestion >= 0
        && items[this.activeSuggestion]
        && this.input.getAttribute('aria-activedescendant') === items[this.activeSuggestion].id
      ) {
        event.preventDefault();
        this.selectSuggestion(items[this.activeSuggestion]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSuggestions();
      }
    }

    async updateSuggestions() {
      if (this.suppressSuggestionsUntilInput) {
        this.closeSuggestions();
        return;
      }
      const ownSerial = ++this.suggestionSerial;
      const query = this.input.value.trim().slice(0, MAX_QUERY_LENGTH);
      if (query.length < 2 || !core()) {
        this.closeSuggestions();
        return;
      }
      try {
        const { base } = await this.datasets(false);
        if (ownSerial !== this.suggestionSerial || this.destroyed) return;
        this.ensureVocabulary(base);
        const raw = core().suggest(query, this.vocabulary, base.products, {
          limit: MAX_SUGGESTIONS,
          locale: locale(),
          context: this.context,
        });
        const safe = (Array.isArray(raw) ? raw : [])
          .filter((item) => (item?.type === 'term' || item?.type === 'query') && item.term)
          .slice(0, MAX_SUGGESTIONS)
          .map((item) => ({
            type: item.type,
            term: String(item.term).slice(0, MAX_QUERY_LENGTH),
            label: String(item.label || item.term).slice(0, MAX_QUERY_LENGTH),
          }));
        this.renderSuggestions(safe);
      } catch (error) {
        if (!isAbort(error) && ownSerial === this.suggestionSerial) this.closeSuggestions();
      }
    }

    ensureVocabulary(dataset) {
      const key = `${dataset.key}|${dataset.fetchedAt}`;
      if (this.vocabulary && this.vocabularyKey === key) return this.vocabulary;
      this.vocabulary = core().createVocabulary(
        dataset.products,
        window.TM24SearchVocabularyManual?.[locale()] || {},
      );
      this.vocabularyKey = key;
      return this.vocabulary;
    }

    renderSuggestions(suggestions) {
      if (this.suppressSuggestionsUntilInput) {
        this.closeSuggestions();
        return;
      }
      this.suggestionList.replaceChildren();
      this.activeSuggestion = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (!suggestions.length || !this.input.value.trim()) {
        this.closeSuggestions();
        return;
      }
      suggestions.forEach((suggestion, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'presentation');
        const button = document.createElement('button');
        button.id = `${this.id}-suggestion-${index}`;
        button.className = 'tm24-unified-search__suggestion';
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.dataset.tm24SearchTerm = suggestion.term;
        button.dataset.tm24SearchSuggestionType = suggestion.type;
        appendHighlightedLabel(button, suggestion.label, this.input.value);
        item.append(button);
        this.suggestionList.append(item);
      });
      this.popover.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.live.textContent = interpolate(
        suggestions.length === 1 ? this.root.dataset.suggestionsCountOne : this.root.dataset.suggestionsCountOther,
        { count: suggestions.length },
      );
      this.positionPopover();
    }

    positionPopover() {
      if (this.popover.hidden) return;
      const anchor = this.root.querySelector('[data-tm24-search-combobox]')?.getBoundingClientRect();
      if (!anchor) return;
      const below = Math.max(96, window.innerHeight - anchor.bottom - 8);
      const above = Math.max(96, anchor.top - 8);
      const openAbove = below < 176 && above > below;
      this.popover.style.top = openAbove ? 'auto' : 'calc(100% + 0.375rem)';
      this.popover.style.bottom = openAbove ? 'calc(100% + 0.375rem)' : 'auto';
      this.popover.style.maxHeight = `${Math.min(352, openAbove ? above : below)}px`;
    }

    closeSuggestions() {
      this.suggestionSerial += 1;
      this.popover.hidden = true;
      this.suggestionList.replaceChildren();
      this.activeSuggestion = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    beginLoading(query, preserveFinderFeedback = false) {
      this.clearMoreInteraction();
      const hasUsableResults = Boolean(
        this.stableResultState
        && this.grid.childElementCount > 0
        && this.stableResultState.matches.length > 0
      );
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'true');
      if (hasUsableResults) this.results.dataset.tm24RetainingResults = 'true';
      else delete this.results.dataset.tm24RetainingResults;
      this.loading.hidden = hasUsableResults;
      this.error.hidden = true;
      if (!hasUsableResults) {
        this.summary.textContent = '';
        this.shown.textContent = '';
      }
      this.clearRouting();
      if (this.help) this.help.hidden = true;
      this.more.hidden = true;
      this.resetFiltersButton.hidden = true;
      if (this.diagnosis) this.diagnosis.hidden = true;
      if (this.context === 'finder') {
        const facets = this.facetsContent?.querySelector('[data-tm24-search-facets]');
        facets?.setAttribute('aria-busy', 'true');
        const loadingStatusSelector = preserveFinderFeedback
          ? '[data-tm24-finder-dialog-status]'
          : '[data-tm24-finder-result-summary], [data-tm24-finder-dialog-status]';
        facets?.querySelectorAll(loadingStatusSelector)
          .forEach((node) => { node.textContent = this.root.dataset.filterLoading; });
        if (this.finderDrawerOpen) {
          const drawerStatus = facets?.querySelector('[data-tm24-finder-dialog-status]');
          if (drawerStatus) drawerStatus.textContent = this.root.dataset.filterLoading;
        } else {
          this.live.textContent = this.root.dataset.loadingText;
        }
        this.syncFinderLiveRegions(facets);
      } else {
        this.live.textContent = this.root.dataset.loadingText;
      }
      this.hideNativeGrid();
      this.lastQuery = query;
    }

    async rank(products, query, explicitSort, vocabulary = this.vocabulary, rankOptions = {}) {
      const searchMethod = typeof core().searchChunked === 'function' ? core().searchChunked : core().search;
      const raw = await searchMethod(products, query, {
        locale: locale(),
        context: rankOptions.context || this.context,
        collectionHandle: rankOptions.collectionHandle || this.root.dataset.scopeHandle,
        vocabulary,
        debug: true,
        yieldEvery: 160,
        signal: this.cardController?.signal,
      });
      const results = normalizedResults(raw);
      if (explicitSort) {
        results.sort((left, right) => {
          const a = productOf(left)?._tm24Ordinal ?? Number.MAX_SAFE_INTEGER;
          const b = productOf(right)?._tm24Ordinal ?? Number.MAX_SAFE_INTEGER;
          return a - b;
        });
      }
      return results;
    }

    async verifyFinderScope(query, ownSerial) {
      this.finderQualifiedCount = 0;
      if (this.context !== 'collection' || !this.finderScopePath) return 0;
      if (this.finderScopeHandle && this.finderScopeHandle === this.root.dataset.scopeHandle) return 0;
      const params = previewParams();
      const source = {
        path: this.finderScopePath,
        params,
        key: sourceKey(this.finderScopePath, params),
      };
      try {
        const dataset = await loadCompleteIndex(source, this.routingController.signal, 0, true);
        if (ownSerial !== this.serial || this.destroyed) return 0;
        const vocabulary = core().createVocabulary(
          dataset.products,
          window.TM24SearchVocabularyManual?.[locale()] || {},
        );
        const matches = this.qualified(await this.rank(
          dataset.products,
          query,
          false,
          vocabulary,
          { context: 'finder', collectionHandle: this.finderScopeHandle },
        ));
        if (ownSerial !== this.serial || this.destroyed) return 0;
        this.finderQualifiedCount = matches.length;
        return matches.length;
      } catch (error) {
        if (isAbort(error)) throw error;
        this.finderQualifiedCount = 0;
        return 0;
      }
    }

    qualified(results) {
      return results.filter((result) => {
        const reasons = Array.isArray(result?.reasons) ? result.reasons : [];
        if (reasons.some((reason) => /^(exact_|manufacturer_plus_|approved_diagnosis)/.test(reason))) return true;
        const tokenMatches = result?.debug?.tokenMatches;
        if (Array.isArray(tokenMatches) && tokenMatches.length) {
          return tokenMatches.every((match) => Number(match?.fieldWeight || 0) >= 50);
        }
        return result?.qualified === true;
      });
    }

    fuseNativeSignal(products, nativeProducts) {
      const nativeRanks = new Map();
      nativeProducts.forEach((product, index) => {
        const id = productId(product);
        if (!id || nativeRanks.has(id)) return;
        nativeRanks.set(id, Number.isFinite(product?.nativeRank) ? product.nativeRank : index);
      });
      return products.map((product) => {
        const rank = nativeRanks.get(productId(product));
        return rank == null ? product : { ...product, nativeRank: rank };
      });
    }

    mergeNativeFallback(results, products, nativeProducts, queryPlan, explicitSort) {
      if (!nativeProducts.length) return results;
      const byId = new Map(products.map((product) => [productId(product), product]));
      const seen = new Set(results.map((result) => productId(productOf(result))));
      const technicalSingleton = queryPlan?.technicalTokens?.length > 0 && nativeProducts.length === 1;
      nativeProducts.forEach((nativeProduct, index) => {
        const id = productId(nativeProduct);
        const product = byId.get(id);
        if (!product || seen.has(id)) return;
        seen.add(id);
        results.push({
          product,
          score: 25,
          reasons: ['native_storefront_fallback'],
          matchedVariantId: '',
          nativeRank: Number.isFinite(nativeProduct?.nativeRank) ? nativeProduct.nativeRank : index,
          stableKey: `${product.handle || ''}|${id}`,
          qualified: technicalSingleton,
        });
      });
      if (explicitSort) {
        results.sort((left, right) => (
          (productOf(left)?._tm24Ordinal ?? Number.MAX_SAFE_INTEGER)
          - (productOf(right)?._tm24Ordinal ?? Number.MAX_SAFE_INTEGER)
        ));
      }
      return results;
    }

    async execute(rawQuery, options = {}) {
      const query = String(rawQuery || '').trim().slice(0, MAX_QUERY_LENGTH);
      const finderFilterOnly = this.context === 'finder' && !query && hasFinderFilters(this.finderFilters);
      const ownSerial = ++this.serial;
      this.cardController?.abort();
      this.cardController = new AbortController();
      this.routingController?.abort();
      this.routingController = new AbortController();
      if (!options.keepSuggestions) this.closeSuggestions();
      this.appliedCorrection = '';
      if (query.length < 2 && !finderFilterOnly) {
        if (!query) this.reset({ historyMode: options.historyMode || 'replace' });
        else this.showTooShort(query, this.lastQuery ? 'replace' : 'none');
        return;
      }
      if (!core()) {
        this.showError(query, sourceError('TM24SearchCore unavailable', 'missing_core'));
        return;
      }

      const previousEntryCaptured = (options.historyMode || 'replace') === 'push';
      if (previousEntryCaptured) this.captureScroll(true);
      this.visible = Math.max(this.pageSize, safeInteger(options.visible, this.pageSize));
      this.beginLoading(query, options.preserveFinderFeedback === true);
      this.writeState(query, options.historyMode || 'replace', previousEntryCaptured);

      try {
        const pendingFocusRevalidation = this.focusRevalidation;
        if (pendingFocusRevalidation) {
          try {
            await pendingFocusRevalidation;
          } catch (_) {
            // A focus-only refresh is opportunistic. The confirmed search or
            // filter action below validates against its current controller.
          }
        }
        if (ownSerial !== this.serial || this.destroyed) return;
        let sets = await this.datasets(options.forceIndex === true);
        if (ownSerial !== this.serial || this.destroyed) return;
        this.ensureVocabulary(sets.base);
        let plan = query ? core().buildQueryPlan(query, this.vocabulary) : null;
        this.appliedCorrection = plan?.correctionApplied ? String(plan.correction || '') : '';
        const nativeQuery = String(plan?.correction || plan?.normalized || query).slice(0, MAX_QUERY_LENGTH);
        let nativeProducts = [];
        let nativeAvailable = Boolean(query);
        let nativeComplete = false;
        if (query) {
          try {
            const nativeSignal = await fetchNativeSignal(nativeQuery, this.routingController.signal);
            nativeProducts = nativeSignal.products;
            nativeComplete = nativeSignal.complete;
          } catch (nativeError) {
            if (isAbort(nativeError)) throw nativeError;
            nativeAvailable = false;
            nativeProducts = [];
          }
        }
        if (ownSerial !== this.serial || this.destroyed) return;

        const baseIds = new Set(sets.base.products.map(productId));
        const hasFreshScopeCandidate = nativeProducts.some((product) => (
          !baseIds.has(productId(product))
          && (product.collections || []).some((item) => item?.handle === this.root.dataset.scopeHandle)
        ));
        if (hasFreshScopeCandidate) {
          sets = await this.datasets(true);
          if (ownSerial !== this.serial || this.destroyed) return;
          this.ensureVocabulary(sets.base);
          plan = query ? core().buildQueryPlan(query, this.vocabulary) : null;
          this.appliedCorrection = plan?.correctionApplied ? String(plan.correction || '') : '';
        }
        if (this.context === 'finder') this.adoptFinderCatalogDataset(sets.base);

        const explicitSort = sets.active.params
          ? new URLSearchParams(sets.active.params).has('sort_by')
          : false;
        const activeProducts = this.fuseNativeSignal(sets.active.products, nativeProducts);
        const baseProducts = sets.active === sets.base
          ? activeProducts
          : this.fuseNativeSignal(sets.base.products, nativeProducts);
        let matches = query
          ? await this.rank(activeProducts, query, explicitSort)
          : activeProducts.map((product) => ({
            product,
            score: 0,
            reasons: ['finder_filter_only'],
            stableKey: `${product.handle || ''}|${productId(product)}`,
            qualified: true,
          }));
        if (nativeComplete) {
          matches = this.mergeNativeFallback(matches, activeProducts, nativeProducts, plan, explicitSort);
        }
        let filterReset = false;
        let unfilteredMatches = matches;
        let activeQualified = this.qualified(matches);
        let unfilteredQualified = activeQualified;
        if (sets.filtered && activeQualified.length === 0) {
          unfilteredMatches = await this.rank(baseProducts, query, explicitSort);
          if (nativeComplete) {
            unfilteredMatches = this.mergeNativeFallback(unfilteredMatches, baseProducts, nativeProducts, plan, explicitSort);
          }
          unfilteredQualified = this.qualified(unfilteredMatches);
          filterReset = unfilteredQualified.length > 0;
        }
        if (this.context === 'finder') {
          const queryQualifiedProducts = activeQualified.map(productOf).filter(Boolean);
          const beforeFinderFilters = activeQualified.length;
          this.finderUnfilteredQueryCount = query ? beforeFinderFilters : null;
          this.setFinderFilterProducts(queryQualifiedProducts, query, sets.base);
          activeQualified = activeQualified.filter((result) => (
            productMatchesFinderFilters(productOf(result), this.finderFilters)
          ));
          filterReset = hasFinderFilters(this.finderFilters)
            && activeQualified.length === 0
            && beforeFinderFilters > 0;
          this.renderFinderFacets(queryQualifiedProducts);
        }
        // Only the shared core's qualified set is customer-visible. Weak
        // description-only rows remain available to the ranker as a last-tier
        // signal, but must not inflate totals or suppress a better route.
        this.matches = activeQualified;
        this.lastDataset = sets.active;
        this.resetFiltersButton.hidden = !filterReset;
        await this.renderVisible(ownSerial);
        if (ownSerial !== this.serial || this.destroyed) return;
        if (
          this.context === 'collection'
          && activeQualified.length === 0
          && unfilteredQualified.length === 0
        ) {
          await this.verifyFinderScope(query, ownSerial);
          if (ownSerial !== this.serial || this.destroyed) return;
        } else {
          this.finderQualifiedCount = 0;
        }
        if (
          this.context === 'collection'
          && activeQualified.length === 0
          && unfilteredQualified.length === 0
          && (this.routingAllowed.size > 0 || this.routingAllowedIds.size > 0)
        ) {
          await this.loadRouting(query, ownSerial, nativeAvailable && nativeComplete ? nativeProducts : null);
          if (ownSerial !== this.serial || this.destroyed) return;
        } else {
          this.clearRouting();
        }
        this.finish(query, filterReset);
        if (options.restoreScroll != null) {
          cancelAnimationFrame(this.restoreScrollFrame);
          this.restoreScrollFrame = requestAnimationFrame(() => {
            this.restoreScrollFrame = 0;
            if (this.destroyed || ownSerial !== this.serial) return;
            scrollTo(0, options.restoreScroll);
            this.captureScroll();
          });
        }
      } catch (error) {
        if (ownSerial !== this.serial || this.destroyed) return;
        if (isAbort(error)) {
          this.settleAbortedExecution();
          return;
        }
        this.showError(query, error);
      }
    }

    async renderVisible(ownSerial, start = 0, end = this.visible) {
      const visibleResults = this.matches.slice(start, end);
      if (!visibleResults.length) {
        if (start === 0) this.grid.replaceChildren();
        return;
      }

      const pages = new Map();
      visibleResults.forEach((result) => {
        const product = productOf(result);
        if (!product) return;
        const key = `${product._tm24SourceKey}|${product._tm24CardPage}`;
        if (!pages.has(key)) pages.set(key, product);
      });
      const pageProducts = [...pages.values()];
      const templateMaps = new Array(pageProducts.length);
      await pooledPages(pageProducts.map((product, index) => ({ product, index })), async ({ product, index }) => {
        templateMaps[index] = await loadCardPage(product, this.cardController.signal, this.cardTtl);
      }, this.cardController.signal);
      if (ownSerial !== this.serial) return;
      const templates = new Map();
      templateMaps.forEach((map) => map.forEach((value, key) => templates.set(key, value)));

      const fragment = document.createDocumentFragment();
      let firstNewLink = null;
      visibleResults.forEach((result) => {
        const product = productOf(result);
        const template = templates.get(String(product?.id));
        if (!template) throw sourceError('A ranked product has no current server card', 'missing_card');
        const node = template.content.firstElementChild?.cloneNode(true);
        if (!node) throw sourceError('Empty server card template', 'empty_card');
        const matchedVariantId = result?.matchedVariantId;
        if (matchedVariantId) {
          node.querySelectorAll('a[href*="/products/"]').forEach((link) => {
            try {
              const url = new URL(link.getAttribute('href'), location.origin);
              if (url.origin === location.origin && !url.searchParams.has('variant')) {
                url.searchParams.set('variant', String(matchedVariantId));
                link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
              }
            } catch (_) {
              // Keep trusted server URL unchanged if it cannot be parsed.
            }
          });
          const matchedVariant = (Array.isArray(product?.variants) ? product.variants : [])
            .find((variant) => String(variant?.id || '').split('/').pop() === String(matchedVariantId));
          const cartForms = [...node.querySelectorAll('form[action*="/cart/add"]')];
          cartForms.forEach((form) => {
            const idInput = form.querySelector('[name="id"]');
            const canSelectSafely = product?.variantDataComplete === true
              && matchedVariant?.available === true
              && idInput;
            if (canSelectSafely) {
              const normalizedCurrent = String(idInput.value || '').split('/').pop();
              if (cartForms.length === 1 || normalizedCurrent === String(matchedVariantId)) {
                idInput.value = String(matchedVariantId);
                return;
              }
            }
            // Keep the server-rendered product link as the safe CTA when the
            // exact variant cannot be proven complete and currently available.
            form.querySelectorAll('button[type="submit"], input[type="submit"]')
              .forEach((control) => {
                control.disabled = true;
                control.hidden = true;
              });
            if (cartForms.length > 1) form.hidden = true;
          });
        }
        if (!firstNewLink) firstNewLink = node.querySelector('a[href]');
        fragment.append(node);
      });
      if (start === 0) this.grid.replaceChildren(fragment);
      else this.grid.append(fragment);
      return firstNewLink;
    }

    async loadRouting(query, ownSerial, alreadyLoadedNative = null) {
      this.clearRouting();
      this.routing.hidden = false;
      this.routing.setAttribute('aria-busy', 'true');
      this.routingHeading.textContent = this.root.dataset.routingLoading;
      this.live.textContent = this.root.dataset.routingLoading;

      try {
        let nativeQuery = query;
        try {
          const plan = core().buildQueryPlan(query, this.vocabulary);
          nativeQuery = String(plan?.correctedQuery || plan?.corrected || plan?.correction || plan?.query || query).slice(0, MAX_QUERY_LENGTH);
        } catch (_) {
          nativeQuery = query;
        }
        const nativeProducts = Array.isArray(alreadyLoadedNative)
          ? alreadyLoadedNative
          : await fetchCompleteNativeSearch(nativeQuery, this.routingController.signal);
        if (ownSerial !== this.serial) return;

        const candidates = new Map();
        nativeProducts.forEach((product) => {
          (Array.isArray(product.collections) ? product.collections : []).forEach((collection) => {
            const handle = String(collection?.handle || '');
            const id = String(collection?.id || '').split('/').pop();
            if (
              !handle
              || handle === this.root.dataset.scopeHandle
              || (!this.routingAllowed.has(handle) && !this.routingAllowedIds.has(id))
              || this.routingExcluded.has(handle)
              || !collection.url
            ) return;
            const entry = candidates.get(handle) || {
              handle,
              id,
              title: String(collection.title || handle),
              url: String(collection.url),
              nativeRank: Number.MAX_SAFE_INTEGER,
            };
            entry.nativeRank = Math.min(entry.nativeRank, safeInteger(product.nativeRank, Number.MAX_SAFE_INTEGER));
            candidates.set(handle, entry);
          });
        });

        const orderedCandidates = [...candidates.values()].sort((left, right) => (
          left.nativeRank - right.nativeRank || left.handle.localeCompare(right.handle)
        ));
        const verified = [];
        await pooledPages(orderedCandidates, async (candidate) => {
          const path = new URL(candidate.url, location.origin).pathname;
          const params = previewParams();
          const source = { path, params, key: sourceKey(path, params) };
          const dataset = await loadCompleteIndex(source, this.routingController.signal, 0, true);
          const targetVocabulary = core().createVocabulary(
            dataset.products,
            window.TM24SearchVocabularyManual?.[locale()] || {},
          );
          const matches = this.qualified(await this.rank(dataset.products, query, false, targetVocabulary));
          if (!matches.length) return;
          verified.push({
            ...candidate,
            count: matches.length,
            bestScore: Number(matches[0]?.score || 0),
          });
        }, this.routingController.signal);
        if (ownSerial !== this.serial) return;
        this.renderRouting(routingOrder(verified), query);
      } catch (error) {
        if (isAbort(error) || ownSerial !== this.serial) return;
        this.clearRouting();
        this.live.textContent = this.root.dataset.routingError;
      }
    }

    renderRouting(routes, query) {
      this.routingList.replaceChildren();
      if (!routes.length) {
        this.clearRouting();
        return;
      }
      routes.forEach((route) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const url = new URL(route.url, location.origin);
        url.searchParams.set('q', query);
        if (this.appliedCorrection) url.searchParams.set('tm24_correction', this.appliedCorrection);
        link.href = `${url.pathname}${url.search}${url.hash}`;
        link.className = 'tm24-unified-search__routing-link';
        const title = document.createElement('span');
        title.textContent = route.title;
        const count = document.createElement('span');
        count.className = 'tm24-unified-search__routing-count';
        count.textContent = interpolate(
          route.count === 1 ? this.root.dataset.routingOne : this.root.dataset.routingOther,
          { count: route.count },
        );
        link.setAttribute('aria-label', `${route.title}: ${count.textContent}. ${this.root.dataset.routingLink}`);
        link.append(title, count);
        item.append(link);
        this.routingList.append(item);
      });
      this.routingHeading.textContent = this.root.dataset.routingHeading;
      this.routing.hidden = false;
      this.routing.setAttribute('aria-busy', 'false');
    }

    clearRouting() {
      this.routingList?.replaceChildren();
      if (this.routing) {
        this.routing.hidden = true;
        this.routing.setAttribute('aria-busy', 'false');
      }
    }

    finish(query, filterReset) {
      const total = this.matches.length;
      const shown = Math.min(this.visible, total);
      const hasQuery = Boolean(query);
      if (this.context === 'finder') {
        this.summary.textContent = this.finderResultSummaryText(total, query);
      } else {
        const summaryTemplate = total
          ? (hasQuery
            ? (total === 1 ? this.root.dataset.resultsOne : this.root.dataset.resultsOther)
            : (total === 1 ? this.root.dataset.filterResultsOne : this.root.dataset.filterResultsOther))
          : (hasQuery ? this.root.dataset.noResultsText : this.root.dataset.noFilterResults);
        this.summary.textContent = interpolate(summaryTemplate, { count: total, query });
      }
      this.shown.textContent = total
        ? interpolate(this.root.dataset.shownTemplate, { shown, total })
        : '';
      this.more.hidden = shown >= total;
      if (this.help) {
        const showHelp = total === 0 && !filterReset;
        this.help.hidden = !showHelp;
        if (showHelp && this.finderLink) {
          this.finderLink.hidden = this.finderQualifiedCount === 0;
          const url = new URL(this.finderLink.getAttribute('href'), location.origin);
          url.searchParams.set('q', query);
          url.searchParams.delete('tm24_correction');
          if (this.appliedCorrection) url.searchParams.set('tm24_correction', this.appliedCorrection);
          this.finderLink.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
        }
        if (showHelp && this.adviceLink && this.context === 'collection') {
          const url = new URL(this.adviceLink.getAttribute('href'), location.origin);
          url.searchParams.set('q', query);
          url.searchParams.delete('tm24_correction');
          if (this.appliedCorrection) url.searchParams.set('tm24_correction', this.appliedCorrection);
          this.adviceLink.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
        }
      }
      if (this.diagnosis) {
        this.diagnosis.hidden = !this.matches.some((result) => result?.diagnosisDisclaimer === true);
      }
      this.loading.hidden = true;
      delete this.results.dataset.tm24RetainingResults;
      this.error.hidden = true;
      this.results.setAttribute('aria-busy', 'false');
      if (this.context === 'finder') {
        this.facetsContent?.querySelector('[data-tm24-search-facets]')?.setAttribute('aria-busy', 'false');
        const finderStatus = interpolate(this.root.dataset.filterStatus, {
          results: this.summary.textContent,
          active: this.activeFinderFilterText(),
        });
        const finalStatus = filterReset
          ? `${finderStatus} ${this.root.dataset.filterResetText}`.trim()
          : finderStatus;
        const finderForm = this.facetsContent?.querySelector('[data-tm24-search-facets]');
        const drawerStatus = finderForm?.querySelector('[data-tm24-finder-dialog-status]');
        if (this.finderDrawerOpen && drawerStatus) drawerStatus.textContent = finalStatus;
        else this.live.textContent = finalStatus;
        this.syncFinderLiveRegions(finderForm);
      } else {
        this.live.textContent = filterReset
          ? this.root.dataset.filterResetText
          : this.summary.textContent;
      }
      this.writeState(query, 'replace');
      this.stableResultState = Object.freeze({
        matches: this.matches,
        visible: this.visible,
        lastQuery: this.lastQuery,
        lastDataset: this.lastDataset,
        summary: this.summary.textContent,
        shown: this.shown.textContent,
        moreHidden: this.more.hidden,
        resetFiltersHidden: this.resetFiltersButton.hidden,
        diagnosisHidden: this.diagnosis?.hidden ?? true,
      });
    }

    showTooShort(query, historyMode) {
      this.clearMoreInteraction();
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'false');
      this.loading.hidden = true;
      delete this.results.dataset.tm24RetainingResults;
      this.error.hidden = true;
      this.grid.replaceChildren();
      this.stableResultState = null;
      this.clearRouting();
      if (this.help) this.help.hidden = true;
      this.summary.textContent = this.root.dataset.tooShortText;
      this.shown.textContent = '';
      this.more.hidden = true;
      this.resetFiltersButton.hidden = true;
      if (this.diagnosis) this.diagnosis.hidden = true;
      this.live.textContent = this.root.dataset.tooShortText;
      this.restoreNativeGrid();
      this.writeState(query, historyMode);
    }

    restoreStableResults() {
      const state = this.stableResultState;
      if (!state || !state.matches.length || this.grid.childElementCount === 0) return false;
      this.matches = state.matches;
      this.visible = state.visible;
      this.lastQuery = state.lastQuery;
      this.lastDataset = state.lastDataset;
      this.summary.textContent = state.summary;
      this.shown.textContent = state.shown;
      this.more.hidden = state.moreHidden;
      this.more.disabled = false;
      this.more.removeAttribute('aria-busy');
      this.resetFiltersButton.hidden = state.resetFiltersHidden;
      if (this.diagnosis) this.diagnosis.hidden = state.diagnosisHidden;
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'false');
      this.loading.hidden = true;
      delete this.results.dataset.tm24RetainingResults;
      this.facetsContent?.querySelectorAll('[aria-busy="true"]').forEach((node) => {
        node.setAttribute('aria-busy', 'false');
      });
      return true;
    }

    settleAbortedExecution() {
      const restored = this.restoreStableResults();
      if (!restored) {
        this.results.setAttribute('aria-busy', 'false');
        this.loading.hidden = true;
        delete this.results.dataset.tm24RetainingResults;
      }
      this.facetsHost?.setAttribute('aria-busy', 'false');
      this.facetsContent?.querySelectorAll('[aria-busy="true"]').forEach((node) => {
        node.setAttribute('aria-busy', 'false');
      });
      if (this.facetsRetry) this.facetsRetry.disabled = false;
      this.error.hidden = true;
    }

    showError(query, error) {
      const retained = this.restoreStableResults();
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'false');
      this.loading.hidden = true;
      delete this.results.dataset.tm24RetainingResults;
      if (!retained) {
        this.grid.replaceChildren();
        this.stableResultState = null;
      }
      this.clearRouting();
      if (this.help) this.help.hidden = true;
      if (!retained) {
        this.summary.textContent = '';
        this.shown.textContent = '';
        this.more.hidden = true;
        if (this.diagnosis) this.diagnosis.hidden = true;
      }
      this.error.hidden = false;
      this.errorMessage.textContent = error?.code === 'incomplete_index'
        ? this.root.dataset.incompleteText
        : this.root.dataset.errorText;
      this.live.textContent = this.errorMessage.textContent;
      if (this.context === 'finder') {
        const facets = this.facetsContent?.querySelector('[data-tm24-search-facets]');
        facets?.setAttribute('aria-busy', 'false');
        if (!retained) {
          facets?.querySelectorAll('[data-tm24-finder-result-summary], [data-tm24-finder-dialog-status]')
            .forEach((node) => { node.textContent = this.root.dataset.filterError; });
          facets?.querySelectorAll('[data-tm24-finder-option-count]').forEach((node) => {
            node.textContent = '';
            node.setAttribute('aria-hidden', 'true');
            node.removeAttribute('aria-label');
          });
        }
      }
      if (!retained) {
        this.lastQuery = query;
        this.restoreNativeGrid();
      }
    }

    createMorePointerGuard(event) {
      if (safeInteger(event?.detail) === 0 || this.moreGuard || !document.body) return;
      const rectangle = this.more.getBoundingClientRect();
      if (rectangle.width <= 0 || rectangle.height <= 0) return;
      const guard = document.createElement('div');
      guard.className = 'tm24-unified-search__more-guard';
      guard.setAttribute('aria-hidden', 'true');
      guard.style.left = `${rectangle.left}px`;
      guard.style.top = `${rectangle.top}px`;
      guard.style.width = `${rectangle.width}px`;
      guard.style.height = `${rectangle.height}px`;
      const placeholder = this.more.cloneNode(true);
      placeholder.removeAttribute('data-tm24-search-more');
      placeholder.removeAttribute('id');
      placeholder.hidden = false;
      placeholder.disabled = true;
      placeholder.tabIndex = -1;
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.removeAttribute('aria-busy');
      guard.append(placeholder);
      const block = (guardEvent) => {
        guardEvent.preventDefault();
        guardEvent.stopPropagation();
      };
      ['pointerdown', 'pointerup', 'touchstart', 'touchend', 'click', 'dblclick']
        .forEach((type) => guard.addEventListener(type, block, { capture: true, passive: false }));
      document.body.append(guard);
      this.moreGuard = guard;
    }

    holdMoreInteraction() {
      return new Promise((resolve) => {
        this.moreGuardResolve = resolve;
        this.moreGuardTimer = window.setTimeout(() => {
          this.moreGuardTimer = 0;
          this.moreGuard?.remove();
          this.moreGuard = null;
          const finishHold = this.moreGuardResolve;
          this.moreGuardResolve = null;
          finishHold?.();
        }, MORE_CLICK_GUARD_MS);
      });
    }

    clearMoreInteraction() {
      if (this.moreGuardTimer) clearTimeout(this.moreGuardTimer);
      this.moreGuardTimer = 0;
      this.moreGuard?.remove();
      this.moreGuard = null;
      const finishHold = this.moreGuardResolve;
      this.moreGuardResolve = null;
      finishHold?.();
      if (this.more) {
        this.more.disabled = false;
        this.more.removeAttribute('aria-busy');
      }
    }

    async showMore(event) {
      if (this.more.disabled || this.visible >= this.matches.length) return;
      const ownSerial = this.serial;
      const previousShown = Math.min(this.visible, this.matches.length);
      const targetVisible = Math.min(previousShown + this.pageSize, this.matches.length);
      const keyboardActivation = safeInteger(event?.detail) === 0;
      this.createMorePointerGuard(event);
      this.more.disabled = true;
      this.more.setAttribute('aria-busy', 'true');
      let completed = false;
      let finalPage = false;
      let firstNewLink = null;
      try {
        firstNewLink = await this.renderVisible(ownSerial, previousShown, targetVisible);
        if (ownSerial !== this.serial || this.destroyed) return;
        // Pagination is transactional: URL/history and the public visible count
        // advance only after every new server-rendered card is ready to append.
        this.visible = targetVisible;
        this.finish(this.lastQuery, false);
        this.live.textContent = interpolate(this.root.dataset.moreAdded, {
          count: targetVisible - previousShown,
        });
        completed = true;
        finalPage = targetVisible >= this.matches.length;
        if (finalPage) this.more.hidden = false;
      } catch (error) {
        if (ownSerial !== this.serial || this.destroyed) return;
        // A failed later page must never destroy the already usable results or
        // leave the filter looking busy. Keep the previous page and allow retry.
        this.visible = previousShown;
        this.more.hidden = previousShown >= this.matches.length;
        if (!isAbort(error)) this.live.textContent = this.root.dataset.errorText;
      } finally {
        if (ownSerial !== this.serial || this.destroyed) {
          this.clearMoreInteraction();
          return;
        }
        await this.holdMoreInteraction();
        if (ownSerial !== this.serial || this.destroyed) return;
        this.more.disabled = false;
        this.more.removeAttribute('aria-busy');
        if (completed && finalPage) {
          this.more.hidden = true;
          if (keyboardActivation && firstNewLink) firstNewLink.focus({ preventScroll: true });
        }
      }
    }

    retrySearch() {
      this.scopeController.abort();
      this.scopeController = new AbortController();
      this.execute(this.lastQuery || this.input.value, {
        historyMode: 'replace',
        visible: this.visible,
        forceIndex: true,
      });
    }

    revalidateSilently() {
      const query = this.input.value.trim();
      if (!query && !(this.context === 'finder' && hasFinderFilters(this.finderFilters))) return;
      this.scopeController.abort();
      this.scopeController = new AbortController();
      this.execute(query, { historyMode: 'none', visible: this.visible });
    }

    resetFilters() {
      if (this.context === 'finder') {
        this.finderFilters = emptyFinderFilters();
        this.resetFinderDisclosureState();
        this.commitFinderFilters('push');
        return;
      }
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      });
      if (this.lastQuery) url.searchParams.set('q', this.lastQuery);
      if (this.context === 'finder') {
        this.pushHistoryState(url);
        this.facetSignature = this.currentFacetSignature();
        this.visible = this.pageSize;
        this.scopeController.abort();
        this.scopeController = new AbortController();
        this.loadOwnedFacets();
        const query = this.lastQuery || this.input.value.trim();
        if (query) this.execute(query, { historyMode: 'none', visible: this.pageSize });
        return;
      }
      location.assign(url);
    }

    hideNativeGrid() {
      if (this.nativeGrid && !this.nativeGrid.isConnected) {
        this.nativeGrid = null;
        this.nativeState = null;
      }
      if (!this.nativeGrid) {
        const candidate = document.querySelector(this.root.dataset.nativeSelector || '');
        if (candidate && candidate !== this.root && !candidate.contains(this.root)) {
          this.nativeGrid = candidate;
          this.nativeState = {
            hidden: candidate.hidden,
            ariaHidden: candidate.getAttribute('aria-hidden'),
          };
        }
      }
      if (this.nativeGrid) {
        this.nativeGrid.hidden = true;
        this.nativeGrid.setAttribute('aria-hidden', 'true');
      }
      this.hideNativeCounts();
    }

    hideNativeCounts() {
      if (!this.nativeCountSelector) return;
      [...this.nativeCountStates.keys()].forEach((element) => {
        if (!element.isConnected) this.nativeCountStates.delete(element);
      });
      document.querySelectorAll(this.nativeCountSelector).forEach((element) => {
        if (!this.nativeCountStates.has(element)) {
          this.nativeCountStates.set(element, {
            hidden: element.hidden,
            ariaHidden: element.getAttribute('aria-hidden'),
          });
        }
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
      });
    }

    restoreNativeGrid() {
      if (this.nativeGrid && this.nativeState) {
        this.nativeGrid.hidden = this.nativeState.hidden;
        if (this.nativeState.ariaHidden == null) this.nativeGrid.removeAttribute('aria-hidden');
        else this.nativeGrid.setAttribute('aria-hidden', this.nativeState.ariaHidden);
      }
      this.nativeCountStates.forEach((state, element) => {
        if (!element.isConnected) return;
        element.hidden = state.hidden;
        if (state.ariaHidden == null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', state.ariaHidden);
      });
      this.nativeCountStates.clear();
    }

    reset({ historyMode = 'replace' } = {}) {
      const previousEntryCaptured = historyMode === 'push';
      if (previousEntryCaptured) this.captureScroll(true);
      cancelAnimationFrame(this.restoreScrollFrame);
      this.restoreScrollFrame = 0;
      this.serial += 1;
      this.clearMoreInteraction();
      this.cardController?.abort();
      this.routingController?.abort();
      this.closeSuggestions();
      this.visible = this.pageSize;
      this.matches = [];
      this.stableResultState = null;
      this.lastQuery = '';
      this.appliedCorrection = '';
      this.finderQualifiedCount = 0;
      this.finderUnfilteredQueryCount = null;
      this.results.hidden = true;
      this.results.setAttribute('aria-busy', 'false');
      delete this.results.dataset.tm24RetainingResults;
      this.loading.hidden = true;
      this.error.hidden = true;
      this.grid.replaceChildren();
      this.clearRouting();
      if (this.help) this.help.hidden = true;
      this.summary.textContent = '';
      this.shown.textContent = '';
      this.live.textContent = '';
      this.more.hidden = true;
      this.resetFiltersButton.hidden = true;
      if (this.diagnosis) this.diagnosis.hidden = true;
      this.restoreNativeGrid();
      if (this.context === 'finder' && this.finderFilterCatalog) {
        this.setFinderFilterProducts(this.finderScopeProducts);
        this.renderFinderFacets(this.finderScopeProducts);
      }
      this.writeState('', historyMode, previousEntryCaptured);
    }

    destroy() {
      if (this.destroyed) return;
      this.closeFinderDrawer({ returnFocus: false });
      this.destroyed = true;
      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestionTimer);
      cancelAnimationFrame(this.finderCommitFrame);
      clearTimeout(this.finderCommitTimer);
      cancelAnimationFrame(this.historyScrollFrame);
      this.historyScrollFrame = 0;
      cancelAnimationFrame(this.restoreScrollFrame);
      this.restoreScrollFrame = 0;
      clearTimeout(this.facetWatchTimer);
      clearTimeout(this.facetDomTimer);
      this.clearMoreInteraction();
      this.serial += 1;
      this.suggestionSerial += 1;
      this.finderFacetSerial += 1;
      this.cardController?.abort();
      this.routingController?.abort();
      this.scopeController.abort();
      this.finderIndexController.abort();
      this.results?.setAttribute('aria-busy', 'false');
      if (this.loading) this.loading.hidden = true;
      this.facetsHost?.setAttribute('aria-busy', 'false');
      this.facetsContent?.querySelectorAll('[aria-busy="true"]').forEach((node) => {
        node.setAttribute('aria-busy', 'false');
      });
      if (this.facetsRetry) this.facetsRetry.disabled = false;
      this.events.abort();
      this.facetsObserver?.disconnect();
      this.restoreNativeGrid();
      if (this.root.isConnected && this.results.parentElement !== this.root) this.root.append(this.results);
      instances.delete(this.root);
    }
  }

  function mount(scope = document) {
    scope.querySelectorAll?.('[data-tm24-search-root][data-tm24-search-enabled="true"]').forEach((root) => {
      if (instances.has(root)) return;
      const instance = new UnifiedSearch(root);
      instances.set(root, instance);
    });
  }

  function unmount(scope) {
    scope.querySelectorAll?.('[data-tm24-search-root]').forEach((root) => instances.get(root)?.destroy());
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);

  const filterTestApi = Object.freeze({
    axes: FINDER_FILTER_AXES,
    normalizeFilterValue,
    emptyFinderFilters,
    parseFinderFilters,
    serializeFinderFilters,
    compactFinderFilters,
    hasFinderFilters,
    productFilterValues,
    productMatchesFinderFilters,
    buildFinderFilterCatalog,
    finderFilterDisplayLabel,
    finderOptionCount,
    buildFinderFacetCounts,
    routingOrder,
    sessionPayload,
    controller: Object.freeze({
      UnifiedSearch,
      indexVersionMatches,
      indexVersionSignature,
      restorePersistentIndex,
      parseIndexVersionShard,
      loadCompleteIndex,
    }),
    constants: Object.freeze({
      INDEX_SCHEMA,
      INDEX_VERSION_SCHEMA,
      NATIVE_SCHEMA,
      SESSION_PREFIX,
      SHARD_REQUEST_TIMEOUT,
      SHARD_RETRY_LIMIT,
      COMPLETE_INDEX_DEADLINE,
      VERSION_VALIDATION_DEADLINE,
      MORE_CLICK_GUARD_MS,
      PERSISTENT_CACHE_SCHEMA,
      MAX_SESSION_INDEX_BYTES,
      MAX_SESSION_INDEX_PRODUCTS,
    }),
  });
  window.TM24FinderFilters = filterTestApi;
  window[GLOBAL_KEY] = { mount, unmount, INDEX_SCHEMA, filters: filterTestApi };
})();
